import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { walletName } from "@/lib/wallet";
import { waNumberFor, waBusinessMessage } from "@/lib/wa";
import { newIdemKey, idemHeaders } from "@/lib/idem";
import { applyBulk, lineFromProduct } from "@/lib/bulkPricing";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import { effectiveCreditMode, CREDIT_MODE_LABELS } from "@/lib/creditMode";
import { openDB, saveCatalogueDelta, loadCatalogue, putConfig, getConfig, putSale, nextReceiptSeq } from "@/lib/offline/db";
import { useConnectivity, isOffline } from "@/lib/offline/connectivity";
import { toast } from "sonner";
import ManagerPinModal from "@/components/ManagerPinModal";
import { isThermalFormat } from "@/lib/printFormat";
import { usePrintFormat } from "@/components/print/PrintFormatDialog";
import { PosRegisterGate, usePosRegister } from "@/components/registers/PosRegisterGate";
import { RegisterSwitchNotice } from "@/components/registers/RegisterSwitchNotice";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { beginInteraction, endInteraction, setDiagModule } from "@/lib/diag";
import { printDoc, itemsTable, totalsBlock, pickupBlock, printYardTicket } from "@/lib/print";
import { FulfillmentPicker, FULFILLMENT, defaultFulfillment } from "@/components/FulfillmentPicker";
import { usePosView } from "@/lib/posView";
import ProductSearchResults, { searchProducts, isExactIdentifier } from "@/components/ProductSearchResults";
import CustomerPickerModal from "@/components/customers/CustomerPickerModal";
import VariantPicker from "@/components/fashion/VariantPicker";
import { ProductCard } from "@/components/pos/ProductCard";
import { imgSrc, IMG_SIZES, imgSizeKey } from "@/lib/img";
import {
  Search, Plus, Minus, Trash2, User, X, CreditCard, Banknote, Receipt, Wallet,
  AlertTriangle, CheckCircle2, ShoppingCart, Printer, Percent, PauseCircle, PlayCircle, PackageCheck, Share2,
} from "lucide-react";
import DocumentShareDialog from "@/components/documents/DocumentShareDialog";

export default function POS() {
  const { business, user } = useAuth();
  const modules = useModules();
  const [posView] = usePosView(user?.id);
  const counter = posView === "counter";
  const full = posView === "full";
  const entryRef = useRef(null);
  const searchRef = useRef(null);
  const newSaleRef = useRef(null);
  // Programmatic refocus (view switch, New Sale, variant picker / picture close) keeps the scanner ready
  // without opening the suggestion panel over the product grid; only a user focus or typing opens it.
  const progFocus = useRef(false);
  const focusScanTarget = () => requestAnimationFrame(() => {
    const el = full ? entryRef.current : searchRef.current;
    progFocus.current = true; el?.focus(); progFocus.current = false;
  });
  useEffect(() => { if (full) focusScanTarget(); }, [full]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Post-sale catalogue refresh is deferred until the receipt is dismissed (sold quantities are applied locally first),
  // so closing the receipt and the next scan are not competing with a 1,500-product fetch + cache rewrite + re-render.
  const refreshPending = useRef(false);
  const refreshTimer = useRef(null);
  const closeReceipt = () => { const cid = beginInteraction("checkout", "new_sale_to_ready"); setReceipt(null); focusScanTarget(); flushDeferredRefresh(); requestAnimationFrame(() => endInteraction(cid, true)); };
  // Print Receipt opens the shared chooser (same pattern as Customer Payment Receipt); nothing prints until a choice is made.
  const { requestPrint: requestReceiptPrint, dialog: receiptPrintDialog } = usePrintFormat("sales_receipt", "Print Receipt");
  // Full Cart entry: Enter adds the exact barcode/SKU match, else the first result; refocus for rapid entry.
  // Phase A-0: local catalogue first (no added latency on hits); on a local miss while online, the indexed server
  // /products/lookup (exact barcode/SKU) then /products/search resolve items added or changed since the catalogue loaded.
  const mergeProducts = (rows) => { if (rows?.length) setProducts((ps) => { const m = new Map(ps.map((x) => [x.id, x])); rows.forEach((r) => m.set(r.id, { ...(m.get(r.id) || {}), ...r })); return [...m.values()]; }); };
  const serverResolve = async (s) => {
    if (conn.status !== "ONLINE") return null;
    try { const { data } = await api.get("/products/lookup", { params: { code: s } }); mergeProducts([data]); return data; } catch { /* not an exact code */ }
    if (serverMissRef.current === s) return null;   // the typed-search fallback already asked the server for this term and found nothing
    try { const { data } = await api.get("/products/search", { params: { q: s, limit: 40 } }); mergeProducts(data); return data.find((x) => isExactIdentifier(x, s)) || data[0] || null; } catch { return null; }
  };
  // B-07: a rapid second scan/Enter must never be silently dropped. `addFromEntry` now (1) captures the
  // typed term and clears the field synchronously, before any network await, so a fast next scan's
  // keystrokes land in a clean box instead of being appended onto this one's still-pending text (the
  // actual way a second legitimate scan was previously lost); and (2) runs its resolve+add-to-cart step
  // through a tiny promise queue so two overlapping scans are serialized (both still happen, in order)
  // instead of truly racing on shared state. No change to local-match precedence or OUT-OF-STOCK handling.
  const addQueueRef = useRef(Promise.resolve());
  const addFromEntry = (p) => {
    const term = search.trim();
    const localPick = p || (() => {
      if (!term) return null;
      const list = deferredSearch === search ? filtered : searchProducts(products, term);   // Enter always resolves against the exact current entry
      return list.find((x) => isExactIdentifier(x, term)) || list[0] || null;
    })();
    setSearch("");
    addQueueRef.current = addQueueRef.current.catch(() => {}).then(async () => {
      let pick = localPick;
      if (!pick && term) pick = await serverResolve(term);
      if (!pick) { if (term) toast.error("No product found", { description: term }); return; }
      if (pick.track_inventory !== false && pick.quantity <= 0) { toast.error("OUT OF STOCK", { description: pick.name }); return; }
      addToCart(pick);
      loadQuickPicks();
    });
    requestAnimationFrame(() => entryRef.current?.focus());
  };
  // Presentation-only class sets (Standard = refined compact; Counter = large, cart-first).
  const v = counter ? {
    grid: "grid lg:grid-cols-[minmax(0,1fr)_420px] xl:grid-cols-[minmax(0,1fr)_560px] 2xl:grid-cols-[minmax(0,1fr)_640px] gap-5",
    products: "grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3",
    pName: "font-semibold text-base leading-snug line-clamp-3", pPrice: "mono font-bold text-lg",
    cartHead: "font-head font-bold text-2xl", pad: "p-5", rows: "space-y-3",
    line: "bg-secondary/50 rounded-2xl p-4 space-y-3", lineName: "text-xl font-bold leading-snug",
    lineMeta: "mono text-base text-muted-foreground", qtyBtn: "h-12 w-12", qtyIcon: "w-5 h-5",
    qtyInput: "h-12 w-24 text-center mono !text-2xl md:!text-2xl font-bold px-1", remove: "w-6 h-6",
    lineTotal: "text-right mono text-2xl font-bold", discBtn: "text-sm px-3 py-1.5",
    row: "text-lg", totalLbl: "font-head font-extrabold text-2xl", total: "mono font-extrabold text-4xl",
    checkout: "h-16 text-2xl font-bold", hold: "h-12 text-base", custBtn: "h-14 text-base",
  } : {
    grid: "grid lg:grid-cols-[1fr_420px] gap-6",
    products: "grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3",
    pName: "font-semibold text-[15px] leading-snug line-clamp-3", pPrice: "mono font-bold",
    cartHead: "font-head font-bold text-lg", pad: "p-4", rows: "space-y-2",
    line: "bg-secondary/50 rounded-xl p-3 space-y-2", lineName: "text-[15px] font-semibold leading-snug",
    lineMeta: "mono text-xs text-muted-foreground", qtyBtn: "h-8 w-8", qtyIcon: "w-3.5 h-3.5",
    qtyInput: "h-8 w-16 text-center mono !text-base md:!text-base font-semibold px-1", remove: "w-4 h-4",
    lineTotal: "text-right mono text-base font-bold", discBtn: "text-[11px] px-2 py-0.5",
    row: "text-sm", totalLbl: "font-head font-bold text-lg", total: "mono font-extrabold text-2xl",
    checkout: "h-12 text-base font-semibold", hold: "h-10", custBtn: "h-11",
  };
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [custOpen, setCustOpen] = useState(false); // shared CustomerPickerModal handles its own search/quick-add state
  const [payOpen, setPayOpen] = useState(false);
  const saleInFlight = useRef(false); const saleIdemKey = useRef(null); const [saleBusy, setSaleBusy] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinReason, setPinReason] = useState("");
  const [pendingSale, setPendingSale] = useState(null);
  const [receipt, setReceipt] = useState(null);
  // Digital Receipt (Issue 1, 2026-10-25): sale is already finalized before this ever opens — purely a
  // read-only share surface, same DocumentShareDialog/CustomerEmailGate used everywhere else. Never re-submits the sale.
  const [shareDoc, setShareDoc] = useState(null);
  const openDigitalReceipt = async () => {
    if (!receipt || receipt.offline_pending) return;
    let contact = { email: "", phone: "" };
    if (receipt.customer_id) {
      try { const { data } = await api.get(`/customers/${receipt.customer_id}`); contact = { email: data?.email || "", phone: waNumberFor(data) }; } catch { /* gate handles missing contact */ }
    }
    setShareDoc({
      id: receipt.id, customerId: receipt.customer_id || null, ...contact,
      label: `${receipt.is_credit ? "Account Sale" : "Sales Receipt"} ${receipt.invoice_number}`,
      waMessage: waBusinessMessage({
        business, kind: "receipt", heading: receipt.is_credit ? "Account Sale" : "Sales Receipt",
        meta: [`Reference: ${receipt.invoice_number}`, `Date: ${fmtDate(receipt.created_at)}`],
        greeting: receipt.customer_name !== "Walk-in" ? receipt.customer_name : "",
        body: receipt.is_credit
          ? [`Total: *${money(receipt.total)}*`, `On Account: *${money(receipt.amount_on_account ?? (receipt.total - (receipt.amount_paid || 0)))}*`, `Due: ${fmtDate(receipt.due_date)}`]
          : [`Total: *${money(receipt.total)}*`, `Status: *Paid in Full*`],
        closing: receipt.is_credit ? "Please settle the balance above by the due date shown." : "Thank you for your business.",
      }),
    });
  };
  const [tendered, setTendered] = useState("");
  const [discount, setDiscount] = useState({ type: "none", value: "", reason: "" });
  const [bestPromo, setBestPromo] = useState(null);
  const [wipay, setWipay] = useState(null);
  const [shiftRequired, setShiftRequired] = useState(false);
  const posReg = usePosRegister();
  const [creditPinOpen, setCreditPinOpen] = useState(false);
  const [qtyDraft, setQtyDraft] = useState({});
  const [lineDiscId, setLineDiscId] = useState(null);
  const [lineDisc, setLineDisc] = useState({ type: "percent", value: "", reason: "" });
  const [clearOpen, setClearOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdRef, setHoldRef] = useState({ reference: "", phone: "" });
  const [heldOpen, setHeldOpen] = useState(false);
  const [heldSales, setHeldSales] = useState([]);
  const [cancelHold, setCancelHold] = useState(null);       // held sale pending cancel
  const [cancelPinOpen, setCancelPinOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [splitMode, setSplitMode] = useState(false);
  const [tenders, setTenders] = useState([]);
  const [delivery, setDelivery] = useState({ enabled: false, fee: "", taxable: false, note: "" });
  const [holdPickup, setHoldPickup] = useState(false);
  const canSeeCost = ["owner", "manager"].includes(user?.role);
  const hydratedRef = useRef(false);
  const cartKey = user ? `neec_cart_${user.business_id}_${user.id}` : null;

  const conn = useConnectivity();
  const offline = conn.status !== "ONLINE" && conn.status !== "SYNCING";
  const regKey = (localStorage.getItem("neec_register_token") || localStorage.getItem("neec_device_token") || "device").slice(0, 16);
  const offDB = () => openDB(user?.business_id, regKey);
  // Picture size (S/M/L) remembered per user and per register; default Medium. Cashiers may change it freely.
  const [imgSize, setImgSize] = useState(() => { const v = localStorage.getItem(imgSizeKey(user?.id, regKey)); return IMG_SIZES[v] ? v : "M"; });
  const changeImgSize = (k) => { setImgSize(k); localStorage.setItem(imgSizeKey(user?.id, regKey), k); focusScanTarget(); };
  const [fullPic, setFullPic] = useState(null);      // {src, name} — view only, never adds to cart
  const [zoomed, setZoomed] = useState(false);
  // Online: refresh the offline catalogue + identity snapshot. Offline: sell from the cached snapshot.
  const cacheMap = useRef(null);   // {product_id: snapshot hash} from the last IndexedDB write
  // Ordering guard: every local stock mutation (sale applied) and every refresh start bumps the epoch. A /products response
  // whose request started before the latest mutation is obsolete — it is discarded (UI + IndexedDB untouched) and a fresh
  // authoritative refresh is re-armed, so an older snapshot can never overwrite a newer sale while the server stays authoritative.
  const stockEpoch = useRef(0);
  const latestLoad = useRef(0);   // epoch of the most recent /products request
  const receiptOpen = useRef(false);
  useEffect(() => { receiptOpen.current = !!receipt; }, [receipt]);
  // B-06: bounded + lightweight automatic fetch (mount, reconnect, every post-sale refresh). 2,000 is
  // comfortably above any real tenant's current catalogue (the comment above already references a
  // ~1,500-product store working from this same local array), so this is a byte-for-byte no-op for every
  // real store today — it only clips a genuinely 8,000+ demo/benchmark catalogue, where it cuts the
  // automatic payload by ~75%. Anything beyond the loaded set is still reached correctly and quickly via
  // the existing indexed /products/lookup (exact scan) and /products/search (typed) server fallbacks below;
  // nothing about checkout's server-side stock/price authority changes.
  const PRODUCTS_AUTO_LOAD_LIMIT = 2000;
  const load = () => {
    const epoch = ++stockEpoch.current; latestLoad.current = epoch;
    api.get("/products", { params: { limit: PRODUCTS_AUTO_LOAD_LIMIT, fields: "lite" } }).then(async (r) => {
      if (epoch !== stockEpoch.current) {   // obsolete response: never applied to UI or IndexedDB
        if (epoch === latestLoad.current) { deferRefresh(); if (!receiptOpen.current) flushDeferredRefresh(); }   // invalidated by a sale -> fetch again; a newer refresh in flight will land otherwise
        return;
      }
      setProducts(r.data);
      try {
        const db = await offDB();
        // B-06: a response at exactly the auto-load cap is a bounded page, not the whole catalogue — tell
        // saveCatalogueDelta so it never purges offline-cached rows that simply sit past the cap (see its comment).
        const truncated = r.data.length >= PRODUCTS_AUTO_LOAD_LIMIT;
        cacheMap.current = await saveCatalogueDelta(db, r.data, cacheMap.current, { truncated });   // writes only changed/removed rows after the first full snapshot
        const sh = await api.get("/shifts/current").then((x) => x.data).catch(() => null);
        await putConfig(db, "identity", { tenant: user.business_id, user_id: user.id, user_name: user.name, role: user.role, business_name: business?.name, tax_rate: business?.tax_rate || 0,
          currency: business?.currency || "JMD", register_key: regKey, shift_id: sh?.id || null, shift_open_at: sh?.opened_at || null, authed_at: new Date().toISOString(), features: modules.modules });
      } catch (e) { console.warn("Offline catalogue sync failed (online POS still works; offline fallback may be stale):", e); }
    }).catch(async () => {
      try { const db = await offDB(); const cached = await loadCatalogue(db); if (cached.length) { setProducts(cached.filter((p) => !p.archived)); toast("Offline — showing cached products", { description: "Cash sales are available. Card, credit and refunds need Internet." }); } }
      catch { /* nothing cached */ }
    });
    api.get("/customers").then((r) => setCustomers((prev) => {
      const byId = new Map(r.data.map((c) => [c.id, c]));
      for (const c of prev) if (!byId.has(c.id)) byId.set(c.id, c);
      return [...byId.values()];
    })).catch(() => {}).finally(() => setCustomersLoaded(true));
    api.get("/wipay/config").then((r) => setWipay(r.data)).catch(() => {});
  };
  useEffect(() => { if (conn.status === "ONLINE") load(); }, [conn.status]); // eslint-disable-line react-hooks/exhaustive-deps
  // Server stays authoritative: the full refresh still happens after every sale — once the receipt is dismissed (or after 20 s if it is left open).
  const applySoldLocally = (items) => { stockEpoch.current++; setProducts((ps) => ps.map((p) => {
    const sold = (items || []).filter((it) => it.product_id === p.id && (it.fulfillment || "take_now") === "take_now").reduce((s, it) => s + (it.quantity || 0), 0);
    return sold && p.track_inventory !== false ? { ...p, quantity: (p.quantity || 0) - sold } : p;
  })); };
  const flushDeferredRefresh = () => {
    if (!refreshPending.current) return;
    refreshPending.current = false; clearTimeout(refreshTimer.current);
    (window.requestIdleCallback || ((cb) => setTimeout(cb, 250)))(() => load(), { timeout: 2000 });
  };
  const deferRefresh = () => { refreshPending.current = true; clearTimeout(refreshTimer.current); refreshTimer.current = setTimeout(flushDeferredRefresh, 20000); };
  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  // Offline CASH checkout: bounded session (12h since last successful auth on this device), open shift cached before the outage.
  const OFFLINE_SESSION_MS = 12 * 60 * 60 * 1000;
  const submitOfflineSale = async () => {
    const db = await offDB(); const ident = await getConfig(db, "identity");
    if (!ident || ident.user_id !== user.id) { toast.error("Offline sales need a register that was signed in before the Internet dropped."); return; }
    if (Date.now() - new Date(ident.authed_at).getTime() > OFFLINE_SESSION_MS) { toast.error("Offline session expired (12 hours). Reconnect to the Internet to continue selling."); return; }
    if (!ident.shift_id) { toast.error("An open shift is required for offline cash sales. Open a shift when the Internet returns."); return; }
    if (usingManual && saleDiscount > 0) { toast.error("Discounts require an Internet connection (Manager/Owner approval is checked online)."); return; }
    if (cart.some((i) => i.discount_type && i.discount_type !== "none")) { toast.error("Item discounts and Set Price require an Internet connection."); return; }
    const tend = parseFloat(tendered || total);
    if (tend < total) { toast.error("Cash tendered is less than the total."); return; }
    const seq = await nextReceiptSeq(db);
    const localNo = `OFF-${new Date().toISOString().slice(2, 10).replace(/-/g, "")}-${ident.register_key.slice(0, 4).toUpperCase()}-${String(seq).padStart(4, "0")}`;
    const items = cart.map((i) => ({ product_id: i.product_id, name: i.name, sku: i.sku, quantity: i.quantity, price: i.price, discount: 0 }));
    const sale = { local_id: crypto.randomUUID(), local_receipt_no: localNo, tenant: user.business_id, register_id: null, register_key: ident.register_key, location_id: null,
      cashier_id: user.id, cashier_name: user.name, shift_id: ident.shift_id, device_id: ident.register_key, items, subtotal: Math.round(subtotal * 100) / 100, tax: Math.round(tax * 100) / 100,
      customer_id: customer?.id || null, customer_name: customer?.name || null,
      total: Math.round(total * 100) / 100, amount_tendered: tend, change: Math.round((tend - total) * 100) / 100, payment_method: "Cash", offline: true, sync_status: "pending", created_at: new Date().toISOString() };
    await putSale(db, sale);
    // Optional local decrement so this register's cached stock reflects what it sold (server remains source of truth).
    setProducts((ps) => ps.map((p) => { const it = items.find((x) => x.product_id === p.id); return it && p.track_inventory !== false ? { ...p, quantity: (p.quantity || 0) - it.quantity } : p; }));
    setReceipt({ id: sale.local_id, invoice_number: localNo, offline_pending: true, register_key: ident.register_key, created_at: sale.created_at, customer_name: "Walk-in", created_by: user.name, items: items.map((i) => ({ ...i })),
      subtotal: sale.subtotal, tax: sale.tax, total: sale.total, amount_tendered: tend, change: sale.change, amount_paid: sale.total, payment_method: "Cash", is_credit: false, original_subtotal: sale.subtotal, sale_discount_amount: 0 });
    setCart([]); setCustomer(null); setPayOpen(false); setTendered(""); setDiscount({ type: "none", value: "", reason: "" }); setBestPromo(null);
    if (cartKey) localStorage.removeItem(cartKey);
    toast.success(`Offline sale ${localNo} saved — will sync when Internet returns`);
  };

  // Handle return from WiPay hosted checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get("wipay");
    const orderId = params.get("order");
    if (!state || !orderId) return;
    window.history.replaceState({}, "", "/pos");
    if (state === "success") {
      api.get(`/wipay/order/${orderId}`).then(({ data }) => {
        if (data.invoice) { setReceipt({ ...data.invoice }); toast.success("WiPay payment confirmed"); load(); }
      });
    } else {
      toast.error(state === "invalid" ? "WiPay signature could not be verified" : "WiPay payment was not completed");
    }
  }, []);

  const wipayReady = wipay?.connected && wipay?.enabled && wipay?.platform_available;

  const payWithWiPay = async () => {
    if (delivery.enabled && deliveryFee > 0) {
      toast.error("Delivery isn't supported with WiPay yet", { description: "Use Cash, Card or Credit for sales that include a delivery fee." });
      return;
    }
    try {
      const { data } = await api.post("/wipay/checkout", {
        items: payloadItems(), customer_id: customer?.id || null,
        discount_type: usingManual ? discount.type : "none",
        discount_value: usingManual ? parseFloat(discount.value || 0) : 0,
        discount_reason: usingManual ? discount.reason : "",
        promotion_id: (!usingManual && promo) ? promo.id : null,
        backend_base: process.env.REACT_APP_BACKEND_URL, frontend_base: window.location.origin,
      });
      window.location.assign(data.checkout_url);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.shift_required) { setPayOpen(false); setShiftRequired(true); }
      else if (detail?.insufficient_stock) { setPayOpen(false); toast.error("INSUFFICIENT STOCK", { description: detail.message }); }
      else { toast.error(apiError(detail)); }
    }
  };

  // Scanner bursts: keystrokes echo immediately; the catalogue scoring (1,500+ products) runs at deferred priority once per burst.
  const deferredSearch = useDeferredValue(search);
  const filtered = useMemo(() => searchProducts(products, deferredSearch).filter((p) => deferredSearch.trim() || !p.parent_id), [products, deferredSearch]);
  // Phase A-0: a typed search with no local match while online asks the indexed server search once (catalogue drift / items added
  // since this register loaded); results merge into the local list so the dropdown and later scans see them. Offline: local only.
  // B-06: when the local catalogue is itself clipped (products.length hit the auto-load cap), ALSO check the server even if the
  // clipped set already has some matches — the clipped set can have a partial hit while a better/exact one sits past the cap.
  const serverMissRef = useRef("");
  // B-07: explicit 250ms debounce (previously relied on useDeferredValue alone, which defers rendering
  // priority but doesn't delay the network call itself) + a sequence guard so a response for a term the
  // user has since typed past can't merge in after a newer term's own request already resolved.
  const searchSeqRef = useRef(0);
  useEffect(() => {
    const s = deferredSearch.trim();
    const clipped = products.length >= PRODUCTS_AUTO_LOAD_LIMIT;
    if (!s || s.length < 2 || (filtered.length && !clipped) || conn.status !== "ONLINE" || serverMissRef.current === s) return;
    const mySeq = ++searchSeqRef.current;
    const t = setTimeout(() => {
      serverMissRef.current = s;
      api.get("/products/search", { params: { q: s, limit: 40 } })
        .then((r) => { if (searchSeqRef.current === mySeq) mergeProducts(r.data); })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [deferredSearch, filtered.length, conn.status]); // eslint-disable-line react-hooks/exhaustive-deps
  // Browse grid cap: with no search term only the first GRID_CAP cards are rendered (large catalogues re-render after every sale).
  // Search, barcode scanning and "Show all" always reach the full catalogue.
  const GRID_CAP = 150;
  const [gridShowAll, setGridShowAll] = useState(false);
  const gridCapped = !deferredSearch.trim() && !gridShowAll && filtered.length > GRID_CAP;
  const gridItems = gridCapped ? filtered.slice(0, GRID_CAP) : filtered;
  const [variantPick, setVariantPick] = useState(null);
  const pickVariant = (v) => { const cid = beginInteraction("product_select", "product_pick"); setVariantPick(null); addToCart({ ...v, name: v.name }); setSearch(""); focusScanTarget(); requestAnimationFrame(() => endInteraction(cid, true)); };
  const [gridDrop, setGridDrop] = useState(false);
  const [fullDrop, setFullDrop] = useState(false);
  // Any pointer press outside the search field + its panel dismisses the panel at once (no blur-timer window).
  useEffect(() => {
    const onDown = (e) => { if (!e.target?.closest?.("[data-pos-search-root]")) { setGridDrop(false); setFullDrop(false); } };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, []);

  const [activeIdx, setActiveIdx] = useState(-1);
  const [quickPicks, setQuickPicks] = useState(null);
  useEffect(() => { setActiveIdx(-1); }, [search]);
  // Single tenant preference gating EVERY recent/frequent suggestion surface (panel, focus, empty search, refetch).
  // Treated as OFF until the business profile has loaded so nothing is ever fetched/cached prematurely.
  const showRecentSales = !!business && business.show_recent_sales !== false;
  const loadQuickPicks = () => showRecentSales ? api.get("/products/quick-picks").then((r) => setQuickPicks(r.data)).catch(() => setQuickPicks({ recent: [], frequent: [] })) : setQuickPicks(null);
  useEffect(() => { if (!showRecentSales) setQuickPicks(null); }, [showRecentSales]);
  const picksForDropdown = showRecentSales ? quickPicks : null;
  // Shared ↑/↓/Enter/Esc handling for every search field.
  const onSearchKey = (e, close) => {
    const n = Math.min(filtered.length, 40);
    if (e.key === "ArrowDown") { e.preventDefault(); if (n) setActiveIdx((i) => (i + 1) % n); }
    else if (e.key === "ArrowUp") { e.preventDefault(); if (n) setActiveIdx((i) => (i <= 0 ? n - 1 : i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); const cid = beginInteraction("product_select", "scan_to_cart"); addFromEntry(activeIdx >= 0 ? filtered[activeIdx] : null); close?.(); requestAnimationFrame(() => endInteraction(cid, true)); }
    else if (e.key === "Escape") { setSearch(""); close?.(); }
  };

  const addToCart = (p) => {
    if (p.is_parent) { setVariantPick(p); return; }   // Fashion: parent selection forces the exact sellable variant
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      const track = p.track_inventory !== false;
      if (ex) {
        if (track && ex.quantity + 1 > (p.quantity ?? Infinity)) {
          toast.error("INSUFFICIENT STOCK", { description: `Only ${p.quantity} ${p.unit || "unit(s)"} of ${p.name} available.` });
          return c;
        }
        return c.map((i) => i.product_id === p.id ? applyBulk({ ...i, quantity: i.quantity + 1 }) : i);
      }
      return [...c, applyBulk({
        product_id: p.id, name: p.name + (p.variant ? ` (${p.variant})` : ""),
        price: p.price, quantity: 1, discount: 0, ...lineFromProduct(p),
        discount_type: "none", discount_value: 0, discount_reason: "", actual_cost: "",
        stock: p.quantity ?? null, track, unit: p.unit || "each",
        fulfillment: holdPickup ? "pickup" : defaultFulfillment(track),
      })];
    });
  };
  const setFulfillment = (id, f) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, fulfillment: f } : i));
  // Stable card callbacks so memoised ProductCards are not re-rendered by cart/search state changes.
  const addToCartRef = useRef(null); addToCartRef.current = addToCart;
  const onCardAdd = useCallback((p) => { const cid = beginInteraction("product_select", "product_pick"); addToCartRef.current(p); requestAnimationFrame(() => endInteraction(cid, true)); }, []);
  useEffect(() => { setDiagModule("product_select"); return () => setDiagModule(null); }, []);   // B2: API calls from this page default to Product selection (route rules refine)
  const openCustomers = () => { const cid = beginInteraction("customers", "customer_lookup"); setCustOpen(true); requestAnimationFrame(() => requestAnimationFrame(() => endInteraction(cid, true))); };
  const onCardFullPic = useCallback((pic) => { setZoomed(false); setFullPic(pic); }, []);
  const toggleHoldAll = () => {
    const on = !holdPickup;
    setHoldPickup(on);
    setCart((c) => c.map((i) => ({ ...i, fulfillment: on ? "pickup" : defaultFulfillment(i.track) })));
  };
  const mixedCart = cart.some((i) => (i.fulfillment || "take_now") !== "take_now");
  const setQty = (id, q) => setCart((c) => c.map((i) => {
    if (i.product_id !== id) return i;
    let v = q;
    if (i.track && i.stock != null && v > i.stock) {
      toast.error("INSUFFICIENT STOCK", { description: `Only ${i.stock} ${i.unit} in stock.` });
      v = i.stock;
    }
    return applyBulk({ ...i, quantity: Math.max(0, v) });
  }));
  // Direct numeric entry: keep a per-line draft string so the field can be cleared
  // while typing; commit valid (> 0) numbers immediately, snap back on blur.
  const onQtyInput = (id, raw) => {
    setQtyDraft((d) => ({ ...d, [id]: raw }));
    if (raw === "") return;
    const n = parseFloat(raw);
    if (isNaN(n) || n <= 0) return;
    setQty(id, n);
  };
  const onQtyBlur = (id) => {
    setQtyDraft((d) => { const { [id]: _, ...rest } = d; return rest; });
    setCart((c) => c.map((i) => (i.product_id === id && (!i.quantity || i.quantity <= 0)) ? { ...i, quantity: 1 } : i));
  };
  const removeItem = (id) => setCart((c) => c.filter((i) => i.product_id !== id));
  const setActualCost = (id, v) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, actual_cost: v } : i));
  const enableDelivery = (on) => {
    if (on && (delivery.fee === "" || delivery.fee == null)) {
      setDelivery({ enabled: true, fee: String(business?.default_delivery_fee ?? 0), taxable: !!business?.default_delivery_taxable, note: "" });
    } else setDelivery((d) => ({ ...d, enabled: on }));
  };

  // Per-line discount math (percent OR fixed line amount), transparent stacking.
  const lineCalc = (i) => {
    const orig = i.price * i.quantity;
    let disc = 0, pct = 0;
    const v = parseFloat(i.discount_value || 0);
    if (i.discount_type === "percent" && v > 0) { pct = Math.min(v, 100); disc = orig * pct / 100; }
    else if (i.discount_type === "fixed" && v > 0) { disc = Math.min(v, orig); pct = orig > 0 ? disc / orig * 100 : 0; }
    else if (i.discount_type === "override" && i.discount_value !== "" && i.discount_value != null) {
      const neg = Math.min(Math.max(v, 0), i.price);
      disc = Math.max(0, i.price - neg) * i.quantity; pct = i.price > 0 ? (i.price - neg) / i.price * 100 : 0;
    }
    const roundedDisc = Math.round(disc * 100) / 100;
    return { orig, disc: roundedDisc, final: Math.round((orig - roundedDisc) * 100) / 100, pct: Math.round(pct * 100) / 100, isOverride: i.discount_type === "override" };
  };
  const openLineDisc = (i) => {
    setLineDiscId(i.product_id);
    setLineDisc(i.discount_type && i.discount_type !== "none" && i.discount_value !== "" && i.discount_value != null
      ? { type: i.discount_type, value: String(i.discount_value), reason: i.discount_reason || "" }
      : { type: "percent", value: "", reason: "" });
  };
  const saveLineDisc = () => {
    const it = cart.find((x) => x.product_id === lineDiscId);
    const v = parseFloat(lineDisc.value || 0);
    if (lineDisc.type === "override") {
      if (!(v >= 0)) { toast.error("Enter a negotiated price"); return; }
      if (it && v > it.price) { toast.error("Negotiated price cannot exceed the regular price"); return; }
    } else {
      if (!(v > 0)) { toast.error("Enter a discount value"); return; }
      if (lineDisc.type === "percent" && v > 100) { toast.error("Percentage cannot exceed 100"); return; }
    }
    if (!lineDisc.reason.trim()) { toast.error("A reason is required"); return; }
    setCart((c) => c.map((i) => i.product_id === lineDiscId
      ? { ...i, discount_type: lineDisc.type, discount_value: v, discount_reason: lineDisc.reason.trim() } : i));
    setLineDiscId(null);
  };
  const clearLineDisc = (id) => setCart((c) => c.map((i) => i.product_id === id
    ? { ...i, discount_type: "none", discount_value: 0, discount_reason: "" } : i));

  // Build the sale item payload (line discounts as per-unit + type/value/reason).
  const payloadItems = () => cart.map((i) => {
    const lc = lineCalc(i);
    return {
      product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity,
      discount: i.quantity > 0 ? lc.disc / i.quantity : 0,
      discount_type: i.discount_type || "none",
      discount_value: parseFloat(i.discount_value || 0),
      discount_reason: i.discount_reason || "",
      actual_cost: (i.actual_cost !== "" && i.actual_cost != null) ? parseFloat(i.actual_cost) : undefined,
      fulfillment: i.fulfillment || defaultFulfillment(i.track),
    };
  });

  const taxRate = business?.tax_rate || 0;
  const promoOn = modules.isEnabled("special_promotions");
  const origSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const lineDiscTotal = cart.reduce((s, i) => s + lineCalc(i).disc, 0);
  const lineSubtotal = Math.max(0, origSubtotal - lineDiscTotal);
  const hasLineDisc = lineDiscTotal > 0.005;
  const usingManual = discount.type !== "none" && parseFloat(discount.value || 0) > 0;
  const manualAmount = discount.type === "percent"
    ? lineSubtotal * Math.min(Math.max(parseFloat(discount.value || 0), 0), 100) / 100
    : (discount.type === "fixed" ? Math.min(Math.max(parseFloat(discount.value || 0), 0), lineSubtotal) : 0);
  const promo = (!usingManual && promoOn) ? bestPromo : null;
  const saleDiscount = Math.min(usingManual ? manualAmount : (promo ? promo.discount_amount : 0), lineSubtotal);
  const discountedSubtotal = Math.max(0, lineSubtotal - saleDiscount);
  const subtotal = lineSubtotal;
  const tax = discountedSubtotal * taxRate / 100;
  const deliveryFee = delivery.enabled ? Math.max(0, parseFloat(delivery.fee || 0) || 0) : 0;
  const deliveryTax = delivery.enabled && delivery.taxable ? deliveryFee * taxRate / 100 : 0;
  const total = discountedSubtotal + tax + deliveryFee + deliveryTax;

  // Auto-detect eligible promotions (only when the Promotions module is ON).
  // Sends line-discounted items so promo bases reflect item discounts.
  useEffect(() => {
    if (!promoOn || cart.length === 0) { setBestPromo(null); return; }
    api.post("/promotions/eligible", { items: payloadItems(), customer_id: customer?.id || null })
      .then((r) => setBestPromo(r.data.best)).catch(() => setBestPromo(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, customer, promoOn]);

  const refreshAndSelect = async (cid) => {
    const { data } = await api.get("/customers");
    setCustomers(data);
    const found = data.find((c) => c.id === cid);
    if (found) setCustomer(found);
    return found;
  };

  // Existing pick or freshly-created customer from the shared CustomerPickerModal —
  // append it to our own `customers` list if it's new (no extra round-trip fetch needed,
  // the POST /customers response already carries the full record).
  const onCustomerPicked = (c) => {
    setCustomer(c);
    setCustomers((prev) => (prev.some((x) => x.id === c.id) ? prev : [...prev, c]));
  };

  // POS-0034 Stage 3: the legacy-phone "Update now" dialog updates a customer in the background —
  // keep our own list (and the currently-selected customer, if it's the same one) in sync so
  // re-picking them in this session doesn't show the stale phone or re-trigger the prompt.
  const onLegacyCustomerUpdated = (c) => {
    setCustomers((prev) => prev.map((x) => (x.id === c.id ? c : x)));
    setCustomer((cur) => (cur && cur.id === c.id ? c : cur));
  };

  const enableCredit = async (pin = null) => {
    if (!customer) return;
    try {
      await api.put(`/customers/${customer.id}`, { credit_enabled: true, manager_pin: pin });
      setCreditPinOpen(false);
      await refreshAndSelect(customer.id);
      toast.success("Credit enabled for this customer");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.needs_approval) { setCreditPinOpen(true); throw new Error("Approval required"); }
      toast.error(apiError(d));
    }
  };

  const submitSale = async (isCredit, managerPin = null, payments = null) => {
    if (offline || isOffline()) {
      if (isCredit) { toast.error("Customer credit requires an Internet connection so NEEC can verify the customer's current balance and credit limit."); return; }
      if (paymentMethod !== "Cash" || payments) { toast.error("Internet is required for card and Wallet payments. Cash is available while offline."); return; }
      return submitOfflineSale();
    }
    if (saleInFlight.current) return;   // second tap while the first request is pending does nothing
    saleInFlight.current = true; setSaleBusy(true);
    try { await _submitSaleOnline(isCredit, managerPin, payments); }
    finally { saleInFlight.current = false; setSaleBusy(false); }
  };

  const _submitSaleOnline = async (isCredit, managerPin = null, payments = null) => {
    if (usingManual && saleDiscount > 0 && !discount.reason.trim()) {
      toast.error("Enter a reason for the discount"); return;
    }
    const payload = {
      customer_id: customer?.id || null,
      items: payloadItems(),
      is_credit: isCredit,
      payment_method: paymentMethod,
      purchased_by_type: purchasedBy.type, purchased_by_name: purchasedBy.name, authorized_buyer_id: purchasedBy.buyer_id, purchased_by_note: purchasedBy.note,
      amount_tendered: isCredit ? 0 : parseFloat(tendered || total),
      discount_type: usingManual ? discount.type : "none",
      discount_value: usingManual ? parseFloat(discount.value || 0) : 0,
      discount_reason: usingManual ? discount.reason : "",
      promotion_id: (!usingManual && promo) ? promo.id : null,
      manager_pin: managerPin,
      payments,
      delivery_enabled: delivery.enabled && deliveryFee > 0,
      delivery_fee: deliveryFee,
      delivery_taxable: delivery.taxable,
      delivery_note: delivery.note,
      hold_for_pickup: false,
    };
    try {
      if (!saleIdemKey.current) saleIdemKey.current = newIdemKey();   // one key per intended sale; reused across PIN/approval retries
      const cid = beginInteraction("checkout", "checkout_to_complete");   // diagnostics correlation only; the sale keeps its own Idempotency-Key
      let data;
      try { ({ data } = await api.post("/sales", payload, idemHeaders(saleIdemKey.current))); } catch (err) { endInteraction(cid, false); throw err; }
      saleIdemKey.current = null;
      setReceipt({ ...data.invoice, change: data.change });
      requestAnimationFrame(() => endInteraction(cid, true));
      setCart([]); setCustomer(null); setPayOpen(false); setTendered("");
      setDiscount({ type: "none", value: "", reason: "" }); setBestPromo(null);
      setSplitMode(false); setTenders([]);
      setDelivery({ enabled: false, fee: "", taxable: false, note: "" });
      setHoldPickup(false);
      if (cartKey) localStorage.removeItem(cartKey);
      applySoldLocally(data.invoice.items); deferRefresh();
      toast.success(`Sale ${data.invoice.invoice_number} completed`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.shift_required) {
        setPayOpen(false); setPinOpen(false); setShiftRequired(true);
      } else if (detail?.insufficient_stock) {
        setPayOpen(false); setPinOpen(false);
        toast.error("INSUFFICIENT STOCK", { description: detail.message });
      } else if (detail?.needs_approval) {
        setPendingSale({ isCredit, payments });
        setPinReason(reasonText(detail));
        setPinOpen(true);
      } else if (detail?.reason === "overpayment") {
        toast.error("OVERPAYMENT", { description: detail.message });
      } else {
        toast.error(apiError(detail));
      }
    }
  };

  // Split / mixed tender — sum applied (unconfirmed bank transfers are NOT received).
  const tenderApplied = (t) => {
    const amt = parseFloat(t.amount || 0) || 0;
    if (t.method === "bank_transfer" && !t.confirmed) return 0;
    return amt;
  };
  const splitApplied = tenders.reduce((s, t) => s + tenderApplied(t), 0);
  const splitChange = tenders.reduce((s, t) => s + (t.method === "cash" ? Math.max(0, (parseFloat(t.cash_given || 0) || 0) - (parseFloat(t.amount || 0) || 0)) : 0), 0);
  const splitRemainder = Math.round((total - splitApplied) * 100) / 100;
  const submitSplit = () => {
    if (tenders.length === 0) { toast.error("Add at least one payment"); return; }
    if (splitApplied > total + 0.01) { toast.error("OVERPAYMENT", { description: "Payments exceed the total. Reduce a tender or give change in cash." }); return; }
    for (const t of tenders) {
      if (!(parseFloat(t.amount || 0) > 0)) { toast.error("Each tender needs an amount"); return; }
      if (t.method === "cash" && (parseFloat(t.cash_given || t.amount) < parseFloat(t.amount))) { toast.error("Cash given is less than the cash applied"); return; }
      if (t.method === "bank_transfer" && t.confirmed && !(t.reference || "").trim()) { toast.error("Confirmed bank transfer needs a reference"); return; }
    }
    const hasRemainder = splitRemainder > 0.005;
    if (hasRemainder && !customer) { toast.error("Select a customer to put the remaining balance on account"); return; }
    const payments = tenders.map((t) => ({
      method: t.method, amount: parseFloat(t.amount || 0),
      cash_given: t.method === "cash" ? parseFloat(t.cash_given || t.amount) : undefined,
      reference: t.method === "bank_transfer" ? (t.reference || "") : undefined,
      note: t.method === "bank_transfer" ? (t.note || "") : undefined,
      confirmed: t.method === "bank_transfer" ? !!t.confirmed : undefined,
    }));
    submitSale(false, null, payments);
  };

  const reasonText = (d) => {
    if (d.reason === "discount_over_limit") return d.message || "Approval required for this discount.";
    const map = {
      exceeds_credit_limit: `This sale (${money(d.total)}) exceeds available credit (${money(d.available_credit)}).`,
      customer_overdue: "Customer has overdue balances. Manager approval needed to extend more credit.",
      credit_disabled_or_hold: "Credit is disabled or account is on hold for this customer.",
    };
    return map[d.reason] || d.message || "Manager approval required.";
  };

  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [prepaidAcct, setPrepaidAcct] = useState(null);
  const [purchasedBy, setPurchasedBy] = useState({ type: "holder", buyer_id: null, name: "", note: "" });
  useEffect(() => {
    if (!customer?.id || !modules.isEnabled("prepaid_accounts")) { setPrepaidAcct(null); if (paymentMethod === "Prepaid") setPaymentMethod("Cash"); return; }
    // Wallet is shown only when it applies to this customer: opted in, or still holding funds (historical balance stays usable).
    api.get(`/prepaid/customers/${customer.id}`).then((r) => setPrepaidAcct((r.data?.customer?.wallet_enabled || r.data?.balance > 0) ? r.data : null)).catch(() => setPrepaidAcct(null));
  }, [customer?.id, modules.isEnabled("prepaid_accounts"), receipt]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Persistent active cart (tenant + user scoped localStorage) ---
  useEffect(() => {
    if (!cartKey || hydratedRef.current) return;
    try {
      const raw = localStorage.getItem(cartKey);
      if (raw) {
        const s = JSON.parse(raw);
        if (s && s.user_id === user.id) {
          if (Array.isArray(s.cart) && s.cart.length) setCart(s.cart);
          if (s.customer) setCustomer(s.customer);
          if (s.discount) setDiscount(s.discount);
          if (s.paymentMethod) setPaymentMethod(s.paymentMethod);
          if (s.tendered) setTendered(s.tendered);
          if (s.notes) setNotes(s.notes);
          if (s.delivery) setDelivery(s.delivery);
        }
      }
    } catch {}
    hydratedRef.current = true;
  }, [cartKey, user]);

  useEffect(() => {
    if (!cartKey || !hydratedRef.current) return;
    if (cart.length === 0) { localStorage.removeItem(cartKey); return; }
    try {
      localStorage.setItem(cartKey, JSON.stringify({
        user_id: user.id, cart, customer, discount, paymentMethod, tendered, notes, delivery, saved_at: Date.now(),
      }));
    } catch {}
  }, [cart, customer, discount, paymentMethod, tendered, notes, delivery, cartKey, user]);

  const clearActiveCart = () => {
    setCart([]); setCustomer(null); setDiscount({ type: "none", value: "", reason: "" });
    setBestPromo(null); setTendered(""); setNotes(""); setPaymentMethod("Cash");
    setDelivery({ enabled: false, fee: "", taxable: false, note: "" });
    setHoldPickup(false);
    if (cartKey) localStorage.removeItem(cartKey);
  };

  // --- Held / suspended sales ---
  const loadHeld = () => api.get("/held-sales").then((r) => setHeldSales(r.data)).catch(() => {});
  useEffect(() => { loadHeld(); }, []);

  const holdSale = async () => {
    if (cart.length === 0) return;
    try {
      await api.post("/held-sales", {
        items: payloadItems(),
        customer_id: customer?.id || null, customer_name: customer?.name || "Walk-in",
        discount_type: usingManual ? discount.type : "none",
        discount_value: usingManual ? parseFloat(discount.value || 0) : 0,
        discount_reason: usingManual ? discount.reason : "",
        promotion_id: (!usingManual && promo) ? promo.id : null,
        notes, reference: holdRef.reference, ref_phone: holdRef.phone,
        cart_total: total,
        delivery_enabled: delivery.enabled, delivery_fee: deliveryFee,
        delivery_taxable: delivery.taxable, delivery_note: delivery.note,
      });
      clearActiveCart();
      setHoldOpen(false); setHoldRef({ reference: "", phone: "" });
      loadHeld();
      toast.success("Sale held. Ready for the next customer");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const resumeHeld = async (h) => {
    try {
      const { data } = await api.post(`/held-sales/${h.id}/resume`);
      // Revalidate against current products/prices before checkout: resolve exactly this held
      // sale's product IDs (a handful of items) instead of re-fetching the whole catalogue.
      const ids = Array.from(new Set((data.items || []).map((it) => it.product_id).filter(Boolean)));
      const prods = ids.length
        ? await api.get("/products", { params: { ids: ids.join(","), fields: "lite" } }).then((r) => r.data).catch(() => [])
        : [];
      mergeProducts(prods);
      const warnings = [];
      const restored = (data.items || []).map((it) => {
        const p = prods.find((x) => x.id === it.product_id);
        if (!p) { warnings.push(`${it.name}: no longer available`); return { ...it, stock: 0, track: true }; }
        const track = p.track_inventory !== false;
        if (track && it.quantity > (p.quantity ?? 0)) warnings.push(`${it.name}: only ${p.quantity} ${p.unit || ""} in stock (cart has ${it.quantity})`);
        const fresh = applyBulk({ ...it, ...lineFromProduct(p), base_price: p.price });
        if (fresh.price !== it.price) warnings.push(`${it.name}: price changed ${money(it.price)} → ${money(fresh.price)}${fresh.bulk_tier_min_qty ? " (bulk tier updated)" : (it.bulk_tier_min_qty ? " (bulk tier no longer applies)" : "")}`);
        return { ...it, stock: p.quantity ?? null, track, unit: p.unit || it.unit || "each", price: it.price };
      });
      setCart(restored);
      setCustomer(data.customer_id ? (customers.find((c) => c.id === data.customer_id) || null) : null);
      setDiscount(data.discount_type && data.discount_type !== "none"
        ? { type: data.discount_type, value: String(data.discount_value || ""), reason: data.discount_reason || "" }
        : { type: "none", value: "", reason: "" });
      setNotes(data.notes || "");
      setDelivery(data.delivery_enabled
        ? { enabled: true, fee: String(data.delivery_fee || ""), taxable: !!data.delivery_taxable, note: data.delivery_note || "" }
        : { enabled: false, fee: "", taxable: false, note: "" });
      setHeldOpen(false);
      loadHeld();
      if (warnings.length) {
        toast.warning("Review before checkout", { description: warnings.join(" · "), duration: 8000 });
      } else {
        toast.success("Held sale resumed");
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const cancelHeld = async (h, pin = null) => {
    try {
      await api.delete(`/held-sales/${h.id}`, { data: { manager_pin: pin } });
      setCancelPinOpen(false); setCancelHold(null);
      loadHeld();
      toast.success("Held sale cancelled");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.needs_approval) { setCancelHold(h); setCancelPinOpen(true); throw new Error("Approval required"); }
      toast.error(apiError(d));
    }
  };

  const printReceipt = (format = "standard") => {
    if (!receipt) return;
    const cur = business?.currency || "JMD $";
    const body = itemsTable(receipt.items, cur, { showFulfillment: !!receipt.pickup_reference }) + totalsBlock({
      items: receipt.items,
      originalSubtotal: receipt.original_subtotal,
      discountAmount: receipt.sale_discount_amount,
      discountLabel: receipt.discount_source === "promotion"
        ? `Promotion: ${receipt.promotion_name || ""}`
        : (receipt.discount_reason ? `Discount (${receipt.discount_reason})` : "Discount"),
      subtotal: receipt.subtotal, tax: receipt.tax, total: receipt.total,
      taxLabel: business?.tax_label || "Tax",
      deliveryFee: receipt.delivery_fee, deliveryTax: receipt.delivery_tax,
      tenders: receipt.tenders,
      tendered: receipt.amount_tendered,
      change: receipt.change_given ?? receipt.change,
      paymentMethod: receipt.payment_method, walletLabel: walletName(business),
      postedToCredit: receipt.is_credit ? (receipt.amount_on_account ?? ((receipt.total || 0) - (receipt.amount_paid || 0))) : null,
      paid: receipt.amount_paid || 0,
      balance: (receipt.total || 0) - (receipt.amount_paid || 0),
    }, cur) + pickupBlock(receipt);
    printDoc({ business, docTitle: receipt.offline_pending ? "Sales Receipt — OFFLINE SALE" : (receipt.is_credit ? "Credit Invoice" : "Sales Receipt"),
      docRef: receipt.offline_pending ? `${receipt.invoice_number} · Pending Sync` : receipt.invoice_number,
      meta: [["Customer", receipt.customer_name], ["Cashier", receipt.created_by || ""], ["Date", new Date().toLocaleDateString()], ...(receipt.prepaid_used ? [["Payment", walletName(business)], ["Wallet Used", money(receipt.prepaid_used)], ["Wallet Balance Remaining", money(receipt.prepaid_balance_after)], ["Purchased By", receipt.purchased_by_name || "Account holder"]] : []), ...(receipt.offline_pending ? [["Status", "OFFLINE SALE — Pending Sync"], ["Register", receipt.register_key || "this register"], ["Local time", new Date(receipt.created_at || Date.now()).toLocaleString()]] : [])],
      // receipt → thermal template only for 80/58; "standard" = Full Page/PDF (inline same-tab frame + system dialog, A4).
      // 80/58 → own window fitted to the roll (same open as Customer Payment Receipt); its print dialog belongs to that window.
      body, showBank: !!receipt.is_credit, format, receipt: isThermalFormat(format), inline: !isThermalFormat(format),
      onDone: () => newSaleRef.current?.focus() });
  };

  return (
    <div className={`${full ? "flex flex-col" : v.grid} h-[calc(100vh-8rem)]`} data-testid="pos-layout" data-view={posView} data-wn-block={cart.length > 0 ? "1" : "0"} data-register-status={posReg.state?.status || ""}>
      <PosRegisterGate state={posReg.state} reload={posReg.reload} />
      <RegisterSwitchNotice state={posReg.state} />
      {full && (
        <Card className="flex flex-col min-h-0 flex-1 p-0 overflow-visible" data-testid="full-cart-view">
          {/* Entry bar (z-raised so the wide results panel can overlay the order table) */}
          <div className="p-4 border-b border-border flex flex-wrap items-center gap-3 relative z-30 rounded-t-xl bg-card">
            <div className="flex-1 min-w-[280px]" data-pos-search-root>
              <Search className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input ref={entryRef} value={search} onChange={(e) => { setSearch(e.target.value); setFullDrop(true); }} data-testid="full-entry-input"
                onFocus={() => { if (!progFocus.current) setFullDrop(true); if (showRecentSales && !quickPicks) loadQuickPicks(); }} onBlur={() => setFullDrop(false)}
                onKeyDown={(e) => onSearchKey(e, () => { if (e.key === "Escape") setFullDrop(false); })}
                placeholder="Search / scan / enter product name, SKU, size or barcode. Enter adds to cart" className="pl-11 h-12 text-base" autoComplete="off" />
              {fullDrop && (search.trim() || picksForDropdown) && (
                <ProductSearchResults results={filtered} query={search} onPick={(p) => { const cid = beginInteraction("product_select", "product_pick"); addFromEntry(p); setFullDrop(false); requestAnimationFrame(() => endInteraction(cid, true)); }} large={false} testPrefix="full-result" wide
                  activeIndex={activeIdx} onHoverIndex={setActiveIdx} quickPicks={picksForDropdown} onDismiss={() => setFullDrop(false)} />
              )}
            </div>
            <div className="min-w-[260px] flex-1 lg:flex-none lg:w-[340px]">
              {customer ? <CustomerBanner customer={customer} total={total} onClear={() => setCustomer(null)} onEnableCredit={() => enableCredit()} wallet={prepaidAcct} business={business} offline={offline} />
                : <Button variant="outline" className="w-full h-12 justify-start" onClick={openCustomers} data-testid="select-customer"><User className="w-4 h-4 mr-2" /> Select Customer</Button>}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" onClick={() => nav("/sales-history")} data-testid="pos-sales-history-btn" className="h-10"><Receipt className="w-4 h-4 mr-1.5" /> Sales History</Button>
              <Button variant="ghost" onClick={() => { loadHeld(); setHeldOpen(true); }} data-testid="held-sales-btn" className="h-10 relative"><PauseCircle className="w-4 h-4 mr-1" /> Held
                {heldSales.length > 0 && <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white" data-testid="held-badge">{heldSales.length}</span>}</Button>
              {cart.length > 0 && <Button variant="ghost" onClick={() => setClearOpen(true)} data-testid="clear-cart" className="h-10 text-rose-500"><Trash2 className="w-4 h-4 mr-1" /> Clear</Button>}
            </div>
          </div>

          {/* Order table */}
          <div className="flex-1 min-h-0 overflow-y-auto pr-14 lg:pr-20" data-testid="full-cart-table">
            <table className="w-full text-sm" data-eli-avoid>
              <thead className="sticky top-0 z-10 bg-card/95 backdrop-blur border-b border-border">
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left font-semibold px-4 py-2 w-10">#</th>
                  <th className="text-left font-semibold px-2 py-2">Product</th>
                  <th className="text-left font-semibold px-2 py-2 hidden xl:table-cell w-36">SKU</th>
                  <th className="text-center font-semibold px-2 py-2 w-44">Qty</th>
                  <th className="text-right font-semibold px-2 py-2 w-32">Unit Price</th>
                  <th className="text-left font-semibold px-2 py-2 w-48">Discount / Set Price</th>
                  <th className="text-right font-semibold px-2 py-2 w-36">Line Total</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {cart.length === 0 && <tr><td colSpan={8} className="text-center text-muted-foreground py-16">Scan or type a product above and press Enter to start the order.</td></tr>}
                {cart.map((i, idx) => {
                  const lc = lineCalc(i); const hasD = lc.disc > 0.005;
                  const prod = products.find((p) => p.id === i.product_id);
                  return (
                    <tr key={i.product_id} className="border-b border-border/70 odd:bg-secondary/20 hover:bg-secondary/40" data-testid={`cart-item-${i.product_id}`}>
                      <td className="px-4 py-1.5 text-muted-foreground mono text-xs">{idx + 1}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                          {imgSrc(prod) && <img src={imgSrc(prod)} alt="" className="w-7 h-7 rounded object-cover shrink-0" />}
                          <div className="min-w-0">
                            <p className="font-semibold text-[15px] leading-tight truncate">{i.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <FulfillmentPicker id={i.product_id} size="xs" value={i.fulfillment || defaultFulfillment(i.track)} onChange={(f) => setFulfillment(i.product_id, f)} />
                              {i.track && i.stock != null && <p className="text-[11px] text-muted-foreground">{i.stock} {i.unit} in stock</p>}
                            </div>
                            {!i.track && canSeeCost && (
                              <div className="flex items-center gap-1.5 mt-0.5" data-testid={`line-cost-row-${i.product_id}`}>
                                <span className="text-[10px] text-muted-foreground whitespace-nowrap">Actual cost</span>
                                <Input type="number" min="0" step="any" value={i.actual_cost ?? ""} placeholder="optional" onChange={(e) => setActualCost(i.product_id, e.target.value)} className="h-6 w-28 !text-xs md:!text-xs mono" data-testid={`line-cost-input-${i.product_id}`} />
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 mono text-xs text-muted-foreground hidden xl:table-cell truncate">{prod?.sku || "-"}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center justify-center gap-1">
                          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setQty(i.product_id, i.quantity - 1)} data-testid={`qty-dec-${i.product_id}`}><Minus className="w-3.5 h-3.5" /></Button>
                          <Input type="number" min="0" step="any" value={qtyDraft[i.product_id] ?? i.quantity} onChange={(e) => onQtyInput(i.product_id, e.target.value)} onBlur={() => onQtyBlur(i.product_id)} onFocus={(e) => e.target.select()}
                            className="h-8 w-16 text-center mono !text-base md:!text-base font-semibold px-1" data-testid={`qty-input-${i.product_id}`} />
                          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setQty(i.product_id, i.quantity + 1)} data-testid={`qty-inc-${i.product_id}`}><Plus className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right mono">{money(i.price)}{i.bulk_tier_min_qty && <span className="block text-[10px] font-semibold text-sky-600 dark:text-sky-400 whitespace-nowrap" data-testid={`bulk-badge-${i.product_id}`}>Bulk {i.bulk_tier_min_qty}+ · reg {money(i.base_price)}</span>}</td>
                      <td className="px-2 py-1.5">
                        <button onClick={() => openLineDisc(i)} data-testid={`line-disc-btn-${i.product_id}`}
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-md px-2 py-1 max-w-full ${hasD ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "text-muted-foreground hover:text-foreground border border-dashed border-border"}`}>
                          <Percent className="w-3 h-3 shrink-0" /><span className="truncate">{hasD ? (lc.isOverride ? `Set Price ${money(i.discount_value)}` : `Item Discount ${lc.pct}%`) : "Discount / Set Price"}</span>
                        </button>
                        {hasD && <p className="text-[10px] text-emerald-700 dark:text-emerald-300 mt-0.5 truncate" data-testid={`line-disc-detail-${i.product_id}`}>− {money(lc.disc)} · {i.discount_reason} <button onClick={() => clearLineDisc(i.product_id)} className="underline" data-testid={`line-disc-clear-${i.product_id}`}>remove</button></p>}
                      </td>
                      <td className="px-2 py-1.5 text-right mono font-bold text-[15px]">
                        {hasD ? <span data-testid={`line-total-${i.product_id}`}><span className="line-through text-muted-foreground text-xs mr-1.5 font-normal">{money(lc.orig)}</span>{money(lc.final)}</span>
                          : <span data-testid={`line-total-${i.product_id}`}>{money(lc.orig)}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-center"><button onClick={() => removeItem(i.product_id)} className="text-muted-foreground hover:text-rose-500 p-1" data-testid={`cart-remove-${i.product_id}`}><X className="w-4 h-4" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sticky totals + checkout */}
          <div className="border-t border-border bg-card p-3 lg:px-5 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="full-cart-footer" data-eli-avoid>
            <span className="text-sm text-muted-foreground" data-testid="full-cart-count">{cart.length} line{cart.length === 1 ? "" : "s"} · {cart.reduce((a, i) => a + i.quantity, 0)} units</span>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm ml-auto">
              <span>Subtotal <b className="mono ml-1">{money(origSubtotal)}</b></span>
              {hasLineDisc && <span>Item Disc. <b className="mono ml-1">− {money(lineDiscTotal)}</b></span>}
              {saleDiscount > 0 && <span>{usingManual ? "Sale Disc." : `Promo`} <b className="mono ml-1">− {money(saleDiscount)}</b></span>}
              {taxRate > 0 && <span>Tax ({taxRate}%) <b className="mono ml-1">{money(tax)}</b></span>}
              {deliveryFee > 0 && <span>Delivery <b className="mono ml-1">{money(deliveryFee)}</b></span>}
              {deliveryTax > 0 && <span>Delivery {business?.tax_label || "Tax"} <b className="mono ml-1">{money(deliveryTax)}</b></span>}
              <span className="font-head font-extrabold text-lg">Total <span className="mono font-extrabold text-3xl text-emerald-600 dark:text-emerald-400 ml-1" data-testid="cart-total">{money(total)}</span></span>
            </div>
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <Button variant="outline" className="h-12 flex-1 lg:flex-none lg:w-36" disabled={cart.length === 0} onClick={() => setHoldOpen(true)} data-testid="hold-sale-btn"><PauseCircle className="w-4 h-4 mr-2" /> Hold Sale</Button>
              <Button className="h-12 flex-1 lg:flex-none lg:w-64 text-lg font-bold bg-emerald-600 hover:bg-emerald-700 text-white" disabled={cart.length === 0} onClick={() => setPayOpen(true)} data-testid="checkout-btn">Checkout · {money(total)}</Button>
            </div>
          </div>
        </Card>
      )}
      {!full && (<>
      {/* Product grid */}
      <div className="flex flex-col min-h-0">
        <div className="relative mb-4 z-20" data-eli-avoid data-pos-search-root>
          <Search className="w-5 h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input ref={searchRef} value={search} onChange={(e) => { setSearch(e.target.value); setGridDrop(true); }} data-testid="pos-search"
            onFocus={() => { if (!progFocus.current) setGridDrop(true); if (showRecentSales && !quickPicks) loadQuickPicks(); }} onBlur={() => setGridDrop(false)}
            onKeyDown={(e) => onSearchKey(e, () => setGridDrop(false))}
            placeholder="Search name, SKU, brand, size (e.g. 36x48) or scan barcode…" className="pl-11 pr-14 xl:pr-40 h-12 text-base" autoComplete="off" />
          <div className="absolute right-[4.5rem] xl:right-44 top-1/2 -translate-y-1/2 hidden md:flex items-center rounded-md border border-border bg-background/80 p-0.5" data-testid="pos-img-size" title="Picture size">
            {Object.keys(IMG_SIZES).map((k) => (
              <button key={k} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => changeImgSize(k)} data-testid={`pos-img-size-${k}`} aria-pressed={imgSize === k}
                className={`h-7 w-7 rounded text-xs font-bold transition-colors ${imgSize === k ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}>{k}</button>
            ))}
          </div>
          <Button variant="outline" onClick={() => nav("/sales-history")} data-testid="pos-sales-history-btn"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 px-2.5 xl:px-4" title="Sales History">
            <Receipt className="w-4 h-4 xl:mr-1.5" /><span className="hidden xl:inline">Sales History</span>
          </Button>
          {gridDrop && (search.trim() || picksForDropdown) && (
            <ProductSearchResults results={filtered} query={search} onPick={(p) => { const cid = beginInteraction("product_select", "product_pick"); addFromEntry(p); setGridDrop(false); requestAnimationFrame(() => endInteraction(cid, true)); }} large={counter} testPrefix="search-result"
              activeIndex={activeIdx} onHoverIndex={setActiveIdx} quickPicks={picksForDropdown} onDismiss={() => setGridDrop(false)} dismissOnLeaveTo="[data-pos-grid-area]" />
          )}
        </div>
        <div className="flex-1 overflow-y-auto pr-1" data-pos-grid-area>
          <div className={full ? v.products : IMG_SIZES[imgSize].cols} data-testid="pos-product-grid" data-img-size={imgSize} data-grid-capped={gridCapped ? "1" : "0"}>
            {gridItems.map((p) => <ProductCard key={p.id} p={p} imgSize={imgSize} counter={counter} onAdd={onCardAdd} onFullPic={onCardFullPic} />)}
          </div>
          {gridCapped && (
            <div className="flex items-center justify-between gap-3 py-3 px-1 text-xs text-muted-foreground" data-testid="pos-grid-cap-note">
              <span>Showing {GRID_CAP} of {filtered.length}{products.length >= PRODUCTS_AUTO_LOAD_LIMIT ? "+" : ""} products — type to search or scan a barcode to reach any product.</span>
              <Button size="sm" variant="outline" onMouseDown={(e) => e.preventDefault()} onClick={() => { setGridShowAll(true); focusScanTarget(); }} data-testid="pos-grid-show-all">Show all</Button>
            </div>
          )}
        </div>
      </div>

      {/* Cart */}
      <Card className="flex flex-col min-h-0 p-0 overflow-hidden" data-testid="cart-panel" data-eli-avoid>
        <div className={`${v.pad} border-b border-border flex items-center justify-between`}>
          <p className={`${v.cartHead} flex items-center gap-2`}><ShoppingCart className="w-5 h-5" /> Cart{cart.length > 0 && <span className="text-muted-foreground font-normal text-sm">· {cart.length} item{cart.length > 1 ? "s" : ""}</span>}</p>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => { loadHeld(); setHeldOpen(true); }} data-testid="held-sales-btn" className="relative">
              <PauseCircle className="w-4 h-4 mr-1" /> Held
              {heldSales.length > 0 && <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white" data-testid="held-badge">{heldSales.length}</span>}
            </Button>
            {cart.length > 0 && <Button variant="ghost" size="sm" onClick={() => setClearOpen(true)} data-testid="clear-cart"><Trash2 className="w-4 h-4" /></Button>}
          </div>
        </div>

        {/* Customer */}
        <div className={`${v.pad} border-b border-border`}>
          {customer ? <CustomerBanner customer={customer} total={total} onClear={() => setCustomer(null)} onEnableCredit={() => enableCredit()} wallet={prepaidAcct} business={business} offline={offline} />
            : <Button variant="outline" className={`w-full ${v.custBtn} justify-start`} onClick={openCustomers} data-testid="select-customer">
                <User className="w-4 h-4 mr-2" /> Select Customer
              </Button>}
        </div>

        <div className={`flex-1 overflow-y-auto ${v.pad} ${v.rows}`}>
          {cart.length === 0 && <p className={`text-center ${v.row} text-muted-foreground py-10`}>Tap products to add them.</p>}
          {cart.map((i) => {
            const lc = lineCalc(i);
            const hasD = lc.disc > 0.005;
            return (
            <div key={i.product_id} className={v.line} data-testid={`cart-item-${i.product_id}`}>
              <div className="flex items-center gap-2">
                {imgSrc(products.find((p) => p.id === i.product_id)) && <img src={imgSrc(products.find((p) => p.id === i.product_id))} alt="" className="w-9 h-9 rounded object-cover shrink-0" data-testid={`cart-thumb-${i.product_id}`} />}
                <div className="flex-1 min-w-0">
                  <p className={`${v.lineName} ${counter ? "line-clamp-2" : "truncate"}`}>{i.name}</p>
                  <p className={v.lineMeta}>{money(i.price)}{i.track && i.stock != null ? ` · ${i.stock} ${i.unit} in stock` : ""}</p>
                  {i.bulk_tier_min_qty && <p className="text-[10px] font-semibold text-sky-600 dark:text-sky-400" data-testid={`bulk-badge-${i.product_id}`}>Bulk Price Applied · {i.bulk_tier_min_qty}+ · Regular {money(i.base_price)}</p>}
                  <div className="mt-1"><FulfillmentPicker id={i.product_id} size="xs" value={i.fulfillment || defaultFulfillment(i.track)} onChange={(f) => setFulfillment(i.product_id, f)} /></div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="outline" className={v.qtyBtn} onClick={() => setQty(i.product_id, i.quantity - 1)} data-testid={`qty-dec-${i.product_id}`}><Minus className={v.qtyIcon} /></Button>
                  <Input
                    type="number" min="0" step="any"
                    value={qtyDraft[i.product_id] ?? i.quantity}
                    onChange={(e) => onQtyInput(i.product_id, e.target.value)}
                    onBlur={() => onQtyBlur(i.product_id)}
                    onFocus={(e) => e.target.select()}
                    className={v.qtyInput} data-testid={`qty-input-${i.product_id}`} />
                  <Button size="icon" variant="outline" className={v.qtyBtn} onClick={() => setQty(i.product_id, i.quantity + 1)} data-testid={`qty-inc-${i.product_id}`}><Plus className={v.qtyIcon} /></Button>
                </div>
                <button onClick={() => removeItem(i.product_id)} className="text-muted-foreground hover:text-rose-500 p-1" data-testid={`cart-remove-${i.product_id}`}><X className={v.remove} /></button>
              </div>
              <div className="flex items-center justify-between">
                <button onClick={() => openLineDisc(i)} data-testid={`line-disc-btn-${i.product_id}`}
                  className={`inline-flex items-center gap-1 font-semibold rounded-md ${v.discBtn} ${hasD ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "text-muted-foreground hover:text-foreground border border-dashed border-border"}`}>
                  <Percent className="w-3 h-3" /> {hasD ? (lc.isOverride ? `Set Price ${money(i.discount_value)}` : `Item Discount ${lc.pct}%`) : "Discount / Set Price"}
                </button>
                <div className={v.lineTotal}>
                  {hasD ? (
                    <span data-testid={`line-total-${i.product_id}`}>
                      <span className="line-through text-muted-foreground text-xs mr-1.5 font-normal">{money(lc.orig)}</span>
                      <span>{money(lc.final)}</span>
                    </span>
                  ) : <span data-testid={`line-total-${i.product_id}`}>{money(lc.orig)}</span>}
                </div>
              </div>
              {hasD && (
                <div className="flex items-center justify-between text-[11px] text-emerald-700 dark:text-emerald-300" data-testid={`line-disc-detail-${i.product_id}`}>
                  <span>− {money(lc.disc)} · {i.discount_reason}</span>
                  <button onClick={() => clearLineDisc(i.product_id)} className="underline hover:no-underline" data-testid={`line-disc-clear-${i.product_id}`}>remove</button>
                </div>
              )}
              {!i.track && canSeeCost && (
                <div className="flex items-center gap-2" data-testid={`line-cost-row-${i.product_id}`}>
                  <span className="text-[11px] text-muted-foreground whitespace-nowrap">Actual cost</span>
                  <Input type="number" min="0" step="any" value={i.actual_cost ?? ""} placeholder="optional: sourced cost"
                    onChange={(e) => setActualCost(i.product_id, e.target.value)}
                    className="h-7 text-xs mono" data-testid={`line-cost-input-${i.product_id}`} />
                </div>
              )}
            </div>
            );
          })}
        </div>

        <div className={`${v.pad} border-t border-border space-y-1.5`} data-eli-avoid>
          <Row label="Subtotal" value={money(origSubtotal)} cls={v.row} />
          {hasLineDisc && <Row label="Item Discounts" value={`− ${money(lineDiscTotal)}`} cls={v.row} />}
          {saleDiscount > 0 && <Row label={usingManual ? "Sale Discount" : `Promo: ${promo?.name || ""}`} value={`− ${money(saleDiscount)}`} cls={v.row} />}
          {hasLineDisc && saleDiscount > 0 && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="cart-stack-note">
              Sale discount applies on top of item discounts (on the reduced subtotal).
            </p>
          )}
          {taxRate > 0 && <Row label={`Tax (${taxRate}%)`} value={money(tax)} cls={v.row} />}
          {deliveryFee > 0 && <Row label="Delivery" value={money(deliveryFee)} cls={v.row} />}
          {deliveryTax > 0 && <Row label={`Delivery ${business?.tax_label || "Tax"}`} value={money(deliveryTax)} cls={v.row} />}
          <div className="flex justify-between items-center pt-1">
            <span className={v.totalLbl}>Total</span>
            <span className={`${v.total} text-emerald-600 dark:text-emerald-400`} data-testid="cart-total">{money(total)}</span>
          </div>
          <Button className={`w-full mt-2 ${v.checkout} bg-emerald-600 hover:bg-emerald-700 text-white`}
            disabled={cart.length === 0} onClick={() => setPayOpen(true)} data-testid="checkout-btn">
            Checkout · {money(total)}
          </Button>
          <Button variant="outline" className={`w-full ${v.hold}`} disabled={cart.length === 0}
            onClick={() => setHoldOpen(true)} data-testid="hold-sale-btn">
            <PauseCircle className="w-4 h-4 mr-2" /> Hold Sale
          </Button>
        </div>
      </Card>
      </>)}

      {/* Clear cart confirm */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="max-w-sm" data-testid="clear-cart-dialog">
          <DialogHeader><DialogTitle className="font-head">Clear all items from this cart?</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Confirm clearing the current cart.</DialogDescription>
          <p className="text-sm text-muted-foreground">This removes every item, the selected customer and any discounts from the current sale. This cannot be undone.</p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setClearOpen(false)} data-testid="clear-cart-cancel">Keep cart</Button>
            <Button variant="destructive" className="flex-1" onClick={() => { clearActiveCart(); setClearOpen(false); }} data-testid="clear-cart-confirm">Clear cart</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hold sale */}
      <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
        <DialogContent className="max-w-sm" data-testid="hold-sale-dialog">
          <DialogHeader><DialogTitle className="font-head">Hold Sale</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Park this cart to serve another customer, then resume it later.</DialogDescription>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{cart.length} item(s) · {money(total)}. Add an optional reference so you can find it later.</p>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Reference / customer name</label>
              <Input value={holdRef.reference} onChange={(e) => setHoldRef({ ...holdRef, reference: e.target.value })} placeholder="e.g. John Brown, getting wallet" data-testid="hold-reference" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Phone (optional)</label>
              <Input value={holdRef.phone} onChange={(e) => setHoldRef({ ...holdRef, phone: e.target.value })} placeholder="Optional" data-testid="hold-phone" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={() => setHoldOpen(false)} data-testid="hold-cancel">Cancel</Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={holdSale} data-testid="hold-confirm">
                <PauseCircle className="w-4 h-4 mr-2" /> Hold & Start New
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Held sales list */}
      <Dialog open={heldOpen} onOpenChange={setHeldOpen}>
        <DialogContent className="max-w-lg" data-testid="held-sales-dialog">
          <DialogHeader><DialogTitle className="font-head">Held Sales</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Resume or cancel a parked sale.</DialogDescription>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {heldSales.length === 0 && <p className="text-center text-sm text-muted-foreground py-8" data-testid="held-empty">No held sales.</p>}
            {heldSales.map((h) => (
              <div key={h.id} className="rounded-xl border border-border p-3 flex items-center gap-3" data-testid={`held-item-${h.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{h.reference || h.customer_name || "Walk-in"}</p>
                  <p className="text-xs text-muted-foreground">
                    {h.item_count} item(s) · {money(h.cart_total)} · {h.cashier_name} · {new Date(h.created_at).toLocaleString()}
                  </p>
                  {h.ref_phone && <p className="text-[11px] text-muted-foreground">{h.ref_phone}</p>}
                </div>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => resumeHeld(h)} data-testid={`held-resume-${h.id}`}>
                  <PlayCircle className="w-4 h-4 mr-1" /> Resume
                </Button>
                <Button size="sm" variant="outline" className="text-rose-500" onClick={() => cancelHeld(h).catch(() => {})} data-testid={`held-cancel-${h.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval to cancel another user's held sale */}
      <ManagerPinModal open={cancelPinOpen} onClose={() => { setCancelPinOpen(false); setCancelHold(null); }}
        title="Approval Required" reason="Manager approval is required to cancel another user's held sale."
        onSubmit={async (pin) => { await cancelHeld(cancelHold, pin); }} />

      {/* Customer picker + Quick Add — shared component, also used by Quotations.js */}
      <CustomerPickerModal
        open={custOpen} onOpenChange={setCustOpen} customers={customers} onSelect={onCustomerPicked}
        title="Select Customer" source="pos_quick_add" loading={!customersLoaded} onCustomerUpdated={onLegacyCustomerUpdated}
        renderBadges={(c) => (c.credit_enabled || c.outstanding_balance > 0) && (
          <p className="text-[11px] mt-1 flex flex-wrap gap-x-3" data-testid={`customer-opt-badges-${c.id}`}>
            {c.credit_enabled && <span className="text-violet-500">Credit: {effectiveCreditMode(c) === "open_account" ? CREDIT_MODE_LABELS.open_account : `${money(c.available_credit ?? 0)} available`}</span>}
            {c.outstanding_balance > 0 && <span className="text-muted-foreground">Owes {money(c.outstanding_balance)}</span>}
          </p>
        )} />

      {/* Enable-credit approval (existing customer with credit disabled) */}
      <ManagerPinModal open={creditPinOpen} onClose={() => setCreditPinOpen(false)}
        title="Approval Required" reason="Manager approval is required to enable credit for this customer."
        onSubmit={async (pin) => { await enableCredit(pin); }} />


      {/* Payment */}
      {/* Per-line item discount */}
      <Dialog open={!!lineDiscId} onOpenChange={(o) => { if (!o) setLineDiscId(null); }}>
        <DialogContent className="max-w-sm" data-testid="line-disc-dialog">
          <DialogHeader><DialogTitle className="font-head">Discount / Set Price</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Apply a percent or fixed discount, or set a negotiated selling price for this line only.</DialogDescription>
          {(() => {
            const it = cart.find((x) => x.product_id === lineDiscId);
            if (!it) return null;
            const orig = it.price * it.quantity;
            const v = parseFloat(lineDisc.value || 0);
            const isOv = lineDisc.type === "override";
            let preview, pct, neg;
            if (isOv) { neg = Math.min(Math.max(v, 0), it.price); preview = Math.max(0, it.price - neg) * it.quantity; }
            else preview = lineDisc.type === "percent" ? Math.min(Math.max(v, 0), 100) / 100 * orig : Math.min(Math.max(v, 0), orig);
            pct = orig > 0 ? Math.round(preview / orig * 10000) / 100 : 0;
            const showPrev = isOv ? (lineDisc.value !== "" && neg < it.price) : v > 0;
            return (
              <div className="space-y-3">
                <p className="text-sm font-medium">{it.name} <span className="text-muted-foreground">· {money(orig)} ({it.quantity} × {money(it.price)})</span></p>
                <div className="flex gap-1">
                  {[["percent", "Percent %"], ["fixed", "Fixed $"], ["override", "Set Price"]].map(([t, lbl]) => (
                    <Button key={t} size="sm" variant={lineDisc.type === t ? "default" : "outline"}
                      className={`h-8 px-2.5 ${lineDisc.type === t ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                      onClick={() => setLineDisc({ ...lineDisc, type: t, value: t === "override" ? String(it.price) : "" })} data-testid={`line-disc-type-${t}`}>{lbl}</Button>
                  ))}
                </div>
                <div>
                  {isOv && <label className="text-[11px] text-muted-foreground">Negotiated unit price (regular {money(it.price)})</label>}
                  <Input type="number" min="0" step="any" value={lineDisc.value}
                    placeholder={isOv ? "e.g. 950" : (lineDisc.type === "percent" ? "e.g. 10" : "e.g. 500")}
                    onChange={(e) => setLineDisc({ ...lineDisc, value: e.target.value })} className="mono" data-testid="line-disc-value" />
                </div>
                {isOv && (
                  <div className="flex flex-wrap gap-1" data-testid="override-reason-presets">
                    {["Negotiated price", "Bulk purchase", "Contractor price", "Owner approved", "Price match", "Other"].map((r) => (
                      <button key={r} onClick={() => setLineDisc({ ...lineDisc, reason: r })}
                        className={`text-[11px] rounded-full px-2 py-0.5 border ${lineDisc.reason === r ? "bg-emerald-600 text-white border-emerald-600" : "border-border text-muted-foreground hover:text-foreground"}`}
                        data-testid={`override-reason-${r.replace(/\s+/g, "-").toLowerCase()}`}>{r}</button>
                    ))}
                  </div>
                )}
                <Input value={lineDisc.reason} placeholder={isOv ? "Reason / note (required)" : "Reason (required)"}
                  onChange={(e) => setLineDisc({ ...lineDisc, reason: e.target.value })} data-testid="line-disc-reason" />
                {showPrev && (
                  <div className="rounded-lg bg-secondary p-2.5 text-sm space-y-0.5" data-testid="line-disc-preview">
                    {isOv && <div className="flex justify-between"><span>Regular</span><span className="mono">{money(it.price)}</span></div>}
                    {isOv && <div className="flex justify-between"><span>Negotiated</span><span className="mono">{money(neg)}</span></div>}
                    <div className="flex justify-between"><span>{isOv ? "Adjustment / each" : "Discount"}</span><span className="mono">− {money(isOv ? (it.price - neg) : preview / (isOv ? 1 : 1))}{isOv ? ` (${pct}%)` : ` (${pct}%)`}</span></div>
                    {isOv && <div className="flex justify-between"><span>Quantity</span><span className="mono">{it.quantity}</span></div>}
                    <div className="flex justify-between font-semibold"><span>Line Total</span><span className="mono">{money(orig - preview)}</span></div>
                    <p className="text-[11px] text-muted-foreground">{isOv ? `Applies to this sale only. The base price stays ${money(it.price)}. ` : (lineDisc.type === "fixed" ? "Fixed amount is checked as " : "")}{`${pct}% off is checked against your discount limit.`}</p>
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1" onClick={() => setLineDiscId(null)} data-testid="line-disc-cancel">Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveLineDisc} data-testid="line-disc-save">Apply</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md" data-testid="payment-modal">
          <DialogHeader><DialogTitle className="font-head">Payment · {money(total)}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Choose a payment method or split across cash, card and bank transfer.</DialogDescription>
          <div className="space-y-4">
            {/* Discount & Promotions */}
            <div className="rounded-xl border border-border p-3 space-y-2" data-testid="pos-discount-block">
              {promo && !usingManual && (
                <div className="rounded-lg bg-violet-500/10 border border-violet-500/30 text-violet-700 dark:text-violet-300 px-3 py-2 text-xs font-semibold" data-testid="pos-promo-banner">
                  Promotion applied: {promo.name} · saves {money(promo.discount_amount)}
                </div>
              )}
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Manual Discount</p>
                <div className="flex gap-1">
                  {[["none", "None"], ["percent", "%"], ["fixed", "$"]].map(([t, lbl]) => (
                    <Button key={t} size="sm" variant={discount.type === t ? "default" : "outline"}
                      className={`h-7 px-2.5 ${discount.type === t ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                      onClick={() => setDiscount({ ...discount, type: t })} data-testid={`pos-disc-type-${t}`}>{lbl}</Button>
                  ))}
                </div>
              </div>
              {discount.type !== "none" && (
                <div className="grid grid-cols-2 gap-2">
                  <Input type="number" value={discount.value} placeholder={discount.type === "percent" ? "e.g. 10" : "e.g. 500"}
                    onChange={(e) => setDiscount({ ...discount, value: e.target.value })} className="mono" data-testid="pos-disc-value" />
                  <Input value={discount.reason} placeholder="Reason (required)"
                    onChange={(e) => setDiscount({ ...discount, reason: e.target.value })} data-testid="pos-disc-reason" />
                </div>
              )}
              {usingManual && bestPromo && promoOn && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400" data-testid="pos-disc-replaces-promo">
                  This manual discount will replace the promotion "{bestPromo.name}".
                </p>
              )}
              {hasLineDisc && (
                <p className="text-[11px] text-muted-foreground" data-testid="pos-linedisc-note">
                  {money(lineDiscTotal)} in item discounts already applied. Any sale discount above stacks on the reduced subtotal.
                </p>
              )}
            </div>
            {/* Delivery (optional, hidden until added) */}
            <div className="rounded-xl border border-border p-3 space-y-2" data-testid="pos-delivery-block">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Delivery</p>
                <Button size="sm" variant={delivery.enabled ? "default" : "outline"}
                  className={`h-7 px-2.5 ${delivery.enabled ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                  onClick={() => enableDelivery(!delivery.enabled)} data-testid="delivery-toggle-btn">
                  {delivery.enabled ? "Remove" : "Add Delivery"}
                </Button>
              </div>
              {delivery.enabled && (
                <div className="space-y-2" data-testid="delivery-fields">
                  <div className="grid grid-cols-2 gap-2 items-end">
                    <div>
                      <label className="text-[11px] text-muted-foreground">Delivery fee</label>
                      <Input type="number" min="0" step="any" value={delivery.fee} placeholder="e.g. 1500"
                        onChange={(e) => setDelivery({ ...delivery, fee: e.target.value })} className="mono h-9" data-testid="delivery-fee" />
                    </div>
                    <label className="flex items-center gap-2 text-xs h-9" data-testid="delivery-taxable-label">
                      <input type="checkbox" checked={delivery.taxable} onChange={(e) => setDelivery({ ...delivery, taxable: e.target.checked })} data-testid="delivery-taxable" />
                      Taxable ({business?.tax_label || "Tax"} {taxRate}%)
                    </label>
                  </div>
                  <Input value={delivery.note} placeholder="Delivery note (optional)"
                    onChange={(e) => setDelivery({ ...delivery, note: e.target.value })} className="h-9" data-testid="delivery-note" />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Delivery {delivery.taxable ? `+ ${business?.tax_label || "Tax"}` : "(non-taxable)"}</span>
                    <span className="mono" data-testid="delivery-line-total">{money(deliveryFee + deliveryTax)}</span>
                  </div>
                </div>
              )}
            </div>
            {/* Hold for Pickup — paid now, collected later (goods reserved on site) */}
            <div className={`rounded-xl border p-3 flex items-center justify-between gap-3 ${mixedCart ? "border-amber-500/50 bg-amber-500/5" : "border-border"}`} data-testid="pos-pickup-block">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><PackageCheck className="w-3.5 h-3.5" /> Hold all for Pickup</p>
                <p className="text-[11px] text-muted-foreground" data-testid="pos-fulfillment-summary">
                  {mixedCart
                    ? Object.entries(FULFILLMENT).map(([k, f]) => { const n = cart.filter((i) => (i.fulfillment || defaultFulfillment(i.track)) === k).length; return n ? `${n} ${f.short}` : null; }).filter(Boolean).join(" · ") + ". One receipt, outstanding lines tracked in Pickups."
                    : "Set each line's method (Now / Hold / Deliver) beside the item, or hold everything."}
                </p>
              </div>
              <Button size="sm" variant={holdPickup ? "default" : "outline"}
                className={`h-7 px-2.5 ${holdPickup ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}`}
                onClick={toggleHoldAll} data-testid="hold-pickup-toggle-btn">
                {holdPickup ? "On" : "Off"}
              </Button>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Method</p>
                <Button size="sm" variant={splitMode ? "default" : "outline"}
                  className={`h-7 px-2.5 ${splitMode ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                  onClick={() => { setSplitMode(!splitMode); if (!splitMode && tenders.length === 0) setTenders([{ method: "cash", amount: String(total.toFixed(2)), cash_given: "", reference: "", note: "", confirmed: false }]); }}
                  data-testid="split-toggle-btn">Split / Mixed</Button>
              </div>
              {!splitMode && (
                <div className="grid grid-cols-2 gap-2">
                  {modules.isEnabled("prepaid_accounts") && customer?.id && prepaidAcct && (
                    <Button variant={paymentMethod === "Prepaid" ? "default" : "outline"} disabled={offline || prepaidAcct.balance <= 0}
                      onClick={() => { if (offline) { toast.error(`${walletName(business)} requires an Internet connection so NEEC can verify the current Wallet balance.`); return; } setPaymentMethod("Prepaid"); }}
                      data-testid="pay-method-Prepaid" className="h-auto min-h-11 py-1.5 col-span-2 justify-between gap-2 text-left" title={offline ? `${walletName(business)} requires an Internet connection so NEEC can verify the current Wallet balance.` : ""}>
                      <span className="flex items-center min-w-0"><Wallet className="w-4 h-4 mr-1.5 shrink-0" /><span className="truncate">Pay with {walletName(business)}</span></span><span className="text-right shrink-0 leading-tight" data-testid="prepaid-available"><span className="block text-[10px] uppercase tracking-wider opacity-70">Available</span><span className="mono text-xs">{offline ? "Internet required" : money(prepaidAcct.balance)}</span></span>
                    </Button>
                  )}
                  {(business?.payment_methods || ["Cash", "WiPay/Card"]).map((m) => (
                    <Button key={m} variant={paymentMethod === m ? "default" : "outline"} onClick={() => setPaymentMethod(m)}
                      data-testid={`pay-method-${m}`} className="h-11">
                      {m.includes("Card") || m.includes("WiPay") ? <CreditCard className="w-4 h-4 mr-1.5" /> : <Banknote className="w-4 h-4 mr-1.5" />}{m}
                    </Button>
                  ))}
                </div>
              )}
            </div>
            {splitMode ? (
              <div className="space-y-2" data-testid="split-payment-block">
                {tenders.map((t, idx) => (
                  <div key={idx} className="rounded-xl border border-border p-2.5 space-y-2" data-testid={`tender-row-${idx}`}>
                    <div className="flex items-center gap-2">
                      <select value={t.method} onChange={(e) => setTenders(tenders.map((x, i) => i === idx ? { ...x, method: e.target.value } : x))}
                        className="h-9 rounded-md border border-border bg-background text-sm px-2 flex-1" data-testid={`tender-method-${idx}`}>
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="bank_transfer">Bank Transfer</option>
                      </select>
                      <Input type="number" value={t.amount} placeholder="Amount"
                        onChange={(e) => setTenders(tenders.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))}
                        className="h-9 mono w-28" data-testid={`tender-amount-${idx}`} />
                      {tenders.length > 1 && <button onClick={() => setTenders(tenders.filter((_, i) => i !== idx))} data-testid={`tender-remove-${idx}`}><X className="w-4 h-4 text-muted-foreground" /></button>}
                    </div>
                    {t.method === "cash" && (
                      <div className="flex items-center gap-2">
                        <Input type="number" value={t.cash_given} placeholder="Cash given"
                          onChange={(e) => setTenders(tenders.map((x, i) => i === idx ? { ...x, cash_given: e.target.value } : x))}
                          className="h-8 mono flex-1" data-testid={`tender-cashgiven-${idx}`} />
                        {parseFloat(t.cash_given || 0) > parseFloat(t.amount || 0) && <span className="text-xs mono text-emerald-500 whitespace-nowrap">change {money(parseFloat(t.cash_given) - parseFloat(t.amount))}</span>}
                      </div>
                    )}
                    {t.method === "bank_transfer" && (
                      <div className="space-y-1.5">
                        <Input value={t.reference} placeholder="Reference number"
                          onChange={(e) => setTenders(tenders.map((x, i) => i === idx ? { ...x, reference: e.target.value } : x))}
                          className="h-8" data-testid={`tender-ref-${idx}`} />
                        <label className="flex items-center gap-2 text-xs" data-testid={`tender-confirmed-label-${idx}`}>
                          <input type="checkbox" checked={!!t.confirmed} onChange={(e) => setTenders(tenders.map((x, i) => i === idx ? { ...x, confirmed: e.target.checked } : x))} data-testid={`tender-confirmed-${idx}`} />
                          Funds confirmed received {!t.confirmed && <span className="text-amber-500">(unconfirmed = not received)</span>}
                        </label>
                      </div>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" className="w-full border-dashed" data-testid="tender-add-btn"
                  onClick={() => setTenders([...tenders, { method: "cash", amount: "", cash_given: "", reference: "", note: "", confirmed: false }])}>
                  <Plus className="w-4 h-4 mr-1" /> Add payment
                </Button>
                <div className="rounded-xl bg-secondary p-2.5 text-sm space-y-1" data-testid="split-summary">
                  <div className="flex justify-between"><span>Total</span><span className="mono">{money(total)}</span></div>
                  <div className="flex justify-between"><span>Paid</span><span className="mono">{money(splitApplied)}</span></div>
                  {splitChange > 0 && <div className="flex justify-between text-emerald-600 dark:text-emerald-400"><span>Change</span><span className="mono">{money(splitChange)}</span></div>}
                  {splitRemainder > 0.005 && <div className="flex justify-between text-amber-600 dark:text-amber-400 font-semibold"><span>On account{customer ? ` (${customer.name})` : ""}</span><span className="mono" data-testid="split-remainder">{money(splitRemainder)}</span></div>}
                  {splitApplied > total + 0.01 && <p className="text-xs text-rose-500 font-semibold" data-testid="split-overpay">Overpayment. Reduce a tender and give change in cash.</p>}
                  {splitRemainder > 0.005 && !customer && <p className="text-xs text-rose-500" data-testid="split-need-customer">Select a customer to put the remainder on account.</p>}
                </div>
                <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
                  disabled={saleBusy || splitApplied > total + 0.01 || (splitRemainder > 0.005 && !customer)}
                  onClick={submitSplit} data-testid="split-complete-btn">
                  {saleBusy ? "Processing…" : <>Complete Sale · {money(total)}</>}
                </Button>
              </div>
            ) : (
            <>
            {paymentMethod === "Prepaid" && prepaidAcct && (
              <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-2 space-y-1.5 text-sm" data-testid="prepaid-checkout-box">
                <div className="flex justify-between"><span>Wallet Balance</span><span className="mono">{money(prepaidAcct.balance)}</span></div>
                <div className="flex justify-between"><span>Wallet Used</span><span className="mono" data-testid="prepaid-used">{money(Math.min(total, prepaidAcct.balance))}</span></div>
                <div className={`flex justify-between font-semibold ${prepaidAcct.balance - total < -0.001 ? "text-rose-500" : ""}`}><span>{prepaidAcct.balance - total < -0.001 ? "Insufficient — use Split / Mixed for the difference" : "Wallet Balance Remaining"}</span><span className="mono" data-testid="prepaid-remaining">{money(Math.max(0, prepaidAcct.balance - total))}</span></div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Purchased by</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[["holder", "Account Holder"], ["buyer", "Authorized Buyer"], ["one_time", "One-Time Buyer"]].map(([v, l]) => <Button key={v} size="sm" variant={purchasedBy.type === v ? "default" : "outline"} className={`h-8 ${purchasedBy.type === v ? "bg-sky-600 hover:bg-sky-700 text-white" : ""}`} onClick={() => setPurchasedBy({ type: v, buyer_id: null, name: "", note: "" })} data-testid={`purchased-by-${v}`}>{l}</Button>)}
                  </div>
                  {purchasedBy.type === "buyer" && <select className="mt-1.5 h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={purchasedBy.buyer_id || ""} onChange={(e) => setPurchasedBy({ ...purchasedBy, buyer_id: e.target.value })} data-testid="purchased-by-buyer-select"><option value="">— choose authorized buyer —</option>{(prepaidAcct.authorized_buyers || []).filter((b) => b.active !== false).map((b) => <option key={b.id} value={b.id}>{b.name}{b.relationship ? ` · ${b.relationship}` : ""}</option>)}</select>}
                  {purchasedBy.type === "one_time" && <div className="grid grid-cols-2 gap-1.5 mt-1.5"><Input placeholder="Buyer name *" value={purchasedBy.name} onChange={(e) => setPurchasedBy({ ...purchasedBy, name: e.target.value })} data-testid="purchased-by-name" /><Input placeholder="ID / note" value={purchasedBy.note} onChange={(e) => setPurchasedBy({ ...purchasedBy, note: e.target.value })} data-testid="purchased-by-note" /></div>}
                </div>
              </div>
            )}
            {paymentMethod === "Cash" && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Amount Tendered</p>
                <Input value={tendered} onChange={(e) => setTendered(e.target.value)} type="number" className="h-12 mono text-lg" data-testid="tendered-input" placeholder={total.toFixed(2)} />
                {tendered && parseFloat(tendered) >= total && (
                  <p className="text-sm mt-1.5">Change: <span className="mono font-bold text-emerald-500">{money(parseFloat(tendered) - total)}</span></p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button className={`h-12 bg-emerald-600 hover:bg-emerald-700 text-white ${customer?.credit_enabled && modules.isEnabled("customers_credit") ? "" : "col-span-2"}`} disabled={saleBusy} onClick={() => submitSale(false)} data-testid="pay-cash-btn">
                <Banknote className="w-4 h-4 mr-1.5" /> {saleBusy ? "Processing…" : "Pay Now"}
              </Button>
              {customer?.credit_enabled && modules.isEnabled("customers_credit") && <Button variant="outline" className="h-12 border-violet-500/40 text-violet-600 dark:text-violet-400 hover:bg-violet-500/10"
                disabled={!customer || offline || saleBusy} onClick={() => submitSale(true)} title={offline ? "Customer credit requires an Internet connection so NEEC can verify the customer's current balance and credit limit." : (!customer ? "Select a customer first" : "")} data-testid="charge-credit-btn">
                <CreditCard className="w-4 h-4 mr-1.5" /> {saleBusy ? "Processing…" : offline ? "Credit (Internet required)" : "Charge to Credit"}
              </Button>}
            </div>
            </>
            )}
            {wipayReady && (
              <Button className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={delivery.enabled && deliveryFee > 0}
                title={delivery.enabled && deliveryFee > 0 ? "Delivery isn't supported with WiPay yet. Use Cash, Card or Credit" : ""}
                onClick={payWithWiPay} data-testid="pay-wipay-btn">
                <CreditCard className="w-4 h-4 mr-1.5" /> Pay with WiPay ({wipay.mode})
              </Button>
            )}
            {customer && !(customer.credit_enabled && modules.isEnabled("customers_credit")) && <p className="text-xs text-muted-foreground text-center" data-testid="credit-not-enabled-hint">{modules.isEnabled("customers_credit") ? `Customer Credit is not enabled for ${customer.name}.` : "Customer Credit is not enabled for this business."} Cash{modules.isEnabled("prepaid_accounts") && prepaidAcct?.balance > 0 ? ", Wallet" : ""} and card payments are available.</p>}
          </div>
        </DialogContent>
      </Dialog>

      <ManagerPinModal open={pinOpen} onClose={() => setPinOpen(false)} title="Approval Required" reason={pinReason}
        onSubmit={async (pin) => {
          try { await submitSale(pendingSale.isCredit, pin, pendingSale.payments || null); setPinOpen(false); }
          catch (e) { throw new Error("Invalid PIN"); }
        }} />

      {/* Receipt — sale is already committed; this modal is presentation only. Printing is an explicit action:
          auto-focus lands on New Sale so a scanner's trailing Enter dismisses instead of printing. */}
      <Dialog open={!!receipt} onOpenChange={(o) => { if (!o) closeReceipt(); }}>
        <DialogContent className="max-w-sm" data-testid="receipt-modal" onOpenAutoFocus={(e) => { e.preventDefault(); newSaleRef.current?.focus(); }}>
          <DialogHeader className="sr-only"><DialogTitle>Sale Complete</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Sale receipt with tenders, totals and print options.</DialogDescription>
          <div className="text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <p className="font-head font-bold text-xl mt-2">Sale Complete</p>
            <p className="text-sm text-muted-foreground mono">{receipt?.invoice_number}</p>
            {receipt?.offline_pending && (
              <div className="mt-2 rounded-lg border-2 border-amber-500/60 bg-amber-500/10 p-2" data-testid="receipt-offline-pending">
                <p className="text-xs font-extrabold tracking-widest text-amber-700 dark:text-amber-400">OFFLINE SALE · PENDING SYNC</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Saved on this register. It will get a normal invoice number when the Internet returns.</p>
              </div>
            )}
            {receipt?.pickup_reference && (
              <div className="mt-2 rounded-lg border-2 border-amber-500/60 bg-amber-500/10 p-2" data-testid="receipt-pickup-required">
                <p className="text-xs font-extrabold tracking-widest text-amber-700 dark:text-amber-400">GOODS OUTSTANDING</p>
                <p className="mono font-bold text-lg">{receipt.pickup_reference}</p>
                {receipt.pickup_qr && <img src={receipt.pickup_qr} alt="Pickup QR" className="w-24 h-24 mx-auto my-1 rounded bg-white p-1" data-testid="receipt-pickup-qr" />}
                <p className="text-[11px] text-muted-foreground" data-testid="receipt-fulfillment-label">{receipt.payment_status} · {receipt.fulfillment_label}. Present this receipt to collect / on delivery.</p>
              </div>
            )}
          </div>
          <div className="border-y border-dashed border-border py-3 my-1 space-y-1.5">
            {receipt?.items?.map((i, idx) => {
              const orig = i.price * i.quantity;
              const lineDisc = (i.discount || 0) * i.quantity;
              const hasD = lineDisc > 0.005;
              return (
                <div key={idx} className="text-sm">
                  <div className="flex justify-between"><span>{i.quantity}× {i.name}
                    {receipt?.pickup_reference && i.fulfillment && <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded ${FULFILLMENT[i.fulfillment]?.cls || ""}`} data-testid={`receipt-line-fulfillment-${idx}`}>{FULFILLMENT[i.fulfillment]?.receipt}</span>}
                  </span><span className="mono">{money(orig)}</span></div>
                  {hasD && (
                    <>
                      <div className="flex justify-between text-xs text-emerald-600 dark:text-emerald-400 pl-4">
                        <span>{i.discount_type === "override" && i.negotiated_price != null
                          ? `Negotiated ${money(i.negotiated_price)} (was ${money(i.price)}, −${i.discount_effective_pct != null ? i.discount_effective_pct : Math.round(lineDisc / orig * 10000) / 100}%)`
                          : `Item Discount ${i.discount_effective_pct != null ? i.discount_effective_pct : Math.round(lineDisc / orig * 10000) / 100}%`}</span>
                        <span className="mono">− {money(lineDisc)}</span>
                      </div>
                      <div className="flex justify-between text-xs font-semibold pl-4">
                        <span>{i.discount_type === "override" && i.negotiated_price != null ? `Line Total (${i.quantity} × ${money(i.negotiated_price)})` : "Line Total"}</span><span className="mono">{money(orig - lineDisc)}</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="space-y-1">
            <Row label="Total" value={money(receipt?.total)} />
            {receipt?.delivery_fee > 0 && <Row label="Delivery" value={money(receipt.delivery_fee)} />}
            {receipt?.delivery_tax > 0 && <Row label={`Delivery ${business?.tax_label || "Tax"}`} value={money(receipt.delivery_tax)} />}
            {receipt?.tenders?.length > 0 && receipt.tenders.map((t, i) => (
              <Row key={i} label={`  ${t.method === "bank_transfer" ? "Bank Transfer" : t.method === "card" ? "Card" : t.method === "prepaid" ? walletName(business) : "Cash"}${t.method === "bank_transfer" && !t.confirmed ? " (unconfirmed)" : ""}`} value={money(t.amount)} />
            ))}
            {receipt?.prepaid_used > 0 && (<>
              <Row label="Wallet Used" value={money(receipt.prepaid_used)} />
              <Row label="Wallet Balance Remaining" value={money(receipt.prepaid_balance_after)} />
              <Row label="Purchased By" value={receipt.purchased_by_name || "Account holder"} />
            </>)}
            <Row label={receipt?.is_credit ? "On account" : "Paid"} value={money(receipt?.is_credit ? (receipt?.amount_on_account ?? ((receipt?.total || 0) - (receipt?.amount_paid || 0))) : receipt?.total)} />
            {receipt?.change > 0 && <Row label="Change" value={money(receipt?.change)} />}
            {receipt?.is_credit && <Row label="Due date" value={receipt?.due_date} />}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" onClick={() => requestReceiptPrint((format) => printReceipt(format))} data-testid="print-receipt"><Printer className="w-4 h-4 mr-2" /> Print Receipt</Button>
            <Button variant="outline" disabled={receipt?.offline_pending} title={receipt?.offline_pending ? "Available once this sale syncs" : ""} onClick={openDigitalReceipt} data-testid="digital-receipt-btn"><Share2 className="w-4 h-4 mr-2" /> Digital Receipt</Button>
          </div>
          <Button ref={newSaleRef} className="w-full mt-2" onClick={closeReceipt} data-testid="receipt-close"><Receipt className="w-4 h-4 mr-2" /> New Sale</Button>
          {receipt?.pickup_reference && (
            <div className="rounded-lg border border-dashed border-amber-500/50 p-2 mt-1 flex items-center justify-between gap-2 text-xs" data-testid="print-yard-ticket-block">
              <span className="font-semibold flex items-center gap-1.5"><Printer className="w-4 h-4" /> Fulfillment / Yard Ticket <span className="mono text-muted-foreground font-normal">{receipt.pickup_reference}</span></span>
              <span className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => printYardTicket(receipt, business, "thermal80", { inline: true })} data-testid="print-yard-ticket-80">80mm</Button>
                <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => printYardTicket(receipt, business, "thermal58", { inline: true })} data-testid="print-yard-ticket-58">58mm</Button>
              </span>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {receiptPrintDialog}
      <DocumentShareDialog open={!!shareDoc} onOpenChange={(o) => !o && setShareDoc(null)}
        title={shareDoc?.label || "Share Receipt"} docLabel={shareDoc?.label} waMessage={shareDoc?.waMessage}
        customerId={shareDoc?.customerId} customerEmail={shareDoc?.email} customerPhone={shareDoc?.phone}
        fileName={`${shareDoc?.label || "document"}.pdf`}
        getPdfBlob={() => api.get(`/sales/${shareDoc.id}/receipt.pdf`, { responseType: "blob" }).then((r) => r.data)}
        onEmail={(idem) => api.post(`/sales/${shareDoc.id}/receipt/email`, { idempotency_key: idem })}
        onWhatsAppShared={(method) => api.post(`/sales/${shareDoc.id}/receipt/whatsapp-shared`, { method }).catch(() => {})} />
      <Dialog open={!!fullPic} onOpenChange={(o) => { if (!o) { setFullPic(null); focusScanTarget(); } }}>
        <DialogContent className="max-w-3xl p-3" data-testid="pos-full-picture">
          <DialogHeader><DialogTitle className="font-head text-base">{fullPic?.name}</DialogTitle><DialogDescription>View only — tap the picture to zoom. Nothing is added to the sale.</DialogDescription></DialogHeader>
          <div className="w-full max-h-[70vh] overflow-auto rounded-lg bg-secondary/40 flex items-center justify-center">
            {fullPic && <img src={fullPic.src} alt={fullPic.name} onClick={() => setZoomed((z) => !z)} data-testid="pos-full-picture-img"
              className={`${zoomed ? "max-w-none w-[200%] cursor-zoom-out" : "max-w-full max-h-[70vh] object-contain cursor-zoom-in"} transition-transform`} />}
          </div>
        </DialogContent>
      </Dialog>
      <VariantPicker parent={variantPick} onPick={pickVariant} onClose={() => { setVariantPick(null); focusScanTarget(); }} />

      {/* Shift required */}
      <Dialog open={shiftRequired} onOpenChange={setShiftRequired}>
        <DialogContent className="max-w-sm" data-testid="shift-required-modal">
          <DialogHeader className="sr-only"><DialogTitle>Shift Required</DialogTitle></DialogHeader>
          <div className="text-center">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center mx-auto"><AlertTriangle className="w-6 h-6" /></div>
            <p className="font-head font-extrabold text-xl mt-3">SHIFT REQUIRED</p>
            <p className="text-sm text-muted-foreground mt-1.5">You must open a cashier shift before completing sales.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button variant="outline" onClick={() => setShiftRequired(false)} data-testid="shift-required-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setShiftRequired(false); nav("/shift"); }} data-testid="shift-required-open">
              <Receipt className="w-4 h-4 mr-2" /> OPEN SHIFT
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, cls = "text-sm" }) {
  return <div className={`flex justify-between ${cls}`}><span className="text-muted-foreground">{label}</span><span className="mono font-medium">{value}</span></div>;
}

function CustomerBanner({ customer, total, onClear, onEnableCredit, wallet, business, offline }) {
  const modules = useModules();
  const isOpen = effectiveCreditMode(customer) === "open_account";
  const creditOn = !!customer.credit_enabled;
  const owes = customer.outstanding_balance > 0;
  const overLimit = creditOn && !isOpen && customer.available_credit != null && total > customer.available_credit;
  const overdue = customer.max_days_overdue > 0;
  const danger = overdue || (creditOn && (overLimit || customer.account_status !== "active"));
  return (
    <div className={`rounded-xl border p-3 ${danger ? "bg-rose-500/10 border-rose-500/30" : "bg-secondary/40 border-border"}`} data-testid="customer-credit-banner">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-sm flex items-center gap-1.5 truncate" data-testid="customer-banner-name">
            {danger && <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />}{customer.name}
          </p>
          <p className="text-xs text-muted-foreground truncate">{[customer.company, customer.phone].filter(Boolean).join(" · ")}</p>
        </div>
        <button onClick={onClear} data-testid="customer-banner-clear"><X className="w-4 h-4 text-muted-foreground" /></button>
      </div>
      {(wallet || creditOn || owes) && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs" data-testid="customer-banner-features">
          {wallet && modules.isEnabled("prepaid_accounts") && <Info label={walletName(business).toUpperCase()} value={offline ? "Online only" : money(wallet.balance)} good />}
          {creditOn && (isOpen ? <Info label="CREDIT" value={CREDIT_MODE_LABELS.open_account.toUpperCase()} good /> : <Info label="CREDIT AVAILABLE" value={money(customer.available_credit ?? 0)} good={!overLimit} />)}
          {owes && <Info label="AMOUNT OWED" value={money(customer.outstanding_balance)} good={!overdue} />}
          {owes && <Info label="OLDEST BALANCE" value={`${customer.oldest_balance_days} DAYS`} good={!overdue} />}
          {creditOn && customer.account_status !== "active" && <Info label="ACCOUNT" value={(customer.account_status || "").toUpperCase()} good={false} />}
        </div>
      )}
      {!creditOn && !owes && !wallet && <p className="text-[11px] text-muted-foreground mt-1" data-testid="customer-banner-regular">Regular customer · purchases are recorded to this customer</p>}
      {!creditOn && owes && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">Credit is not enabled (existing balance still owed).</p>
          <Button size="sm" variant="outline" className="h-7" onClick={onEnableCredit} data-testid="enable-credit-btn">Enable credit</Button>
        </div>
      )}
      {overLimit && <p className="text-xs text-rose-500 font-semibold mt-2">⚠ This sale exceeds available credit. Manager approval required.</p>}
    </div>
  );
}

function Info({ label, value, good }) {
  return (
    <div>
      <p className="text-[9px] font-semibold tracking-wider text-muted-foreground">{label}</p>
      <p className={`mono font-bold ${good === false ? "text-rose-500" : ""}`}>{value}</p>
    </div>
  );
}
